import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { handleApiRequest, apiClient } from '@/services/api/client'
import { isImportCancelled, templateTrainingApi, timetableImportV2Api } from '@/services/api'
import { bulkTimetableImportApi } from '@/services/api/bulkTimetableImport'
import type { TemplateInfo } from '@/services/api/templateTraining'
import { FilesTab, TemplatesTab, EntriesTab, TimetablesTab, SpreadsheetImportTab, EventoSyncTab, PostUploadDialog, TemplateSelectionDialog, ExtractionProgress, TokenMatchingModal, TokenOKDialog, BatchStatusView, UnmatchedTeacherCodeDialog, BatchConflictDialog, BulkImportDialog, BulkImportResult, TeacherAssignmentDialog } from '@/components/timetable-import'
import type { BatchInfo } from '@/components/timetable-import/BatchInfoHeader'
import { BulkImportProgress } from '@/components/timetable-import/BulkImportProgress'
import { BulkShopDialog } from '@/components/timetable-import/BulkShopDialog'
import { ImportValidityDialog } from '@/components/timetable-import/ImportValidityDialog'
import type { TokenMatch, UnmatchedToken, TokenPositions, SelectionResult, ReconstructedEntry, TokenResolution } from '@/components/timetable-import/typesV2'
import type { TokenResolutionContext } from '@/types/tokenResolution'
import { ImportRunDialog } from '@/components/timetable-import/ImportRunDialog'
import { CheckCircle2, FileText, FolderOpen, Target, FileSpreadsheet, Cloud, Loader2, AlertTriangle, ClipboardList, CalendarDays } from 'lucide-react'
import type { UploadedFileEntry, FileStatus, TabType } from '@/components/timetable-import'
import { clearPersistedBatches, usePersistedBatch, PersistedBatchState } from '@/hooks/usePersistedBatch'
import { autoResolutionsFromMatched, importIssueText, importRunLabel, nextContinueAction, pendingTeachersForDialog, shouldSkipTokenMatching, teacherCodeFromTokens, tokenPageBatchInfo, visibleShopItems } from '@/components/timetable-import/continueBatch'

type ExtendedTabType = TabType | 'spreadsheet' | 'evento' | 'entries' | 'timetables'

const EVENTO_SUPPORTED_COUNTRIES = ['CH', 'LI']

const IS_DEV = import.meta.env.DEV

function debugLog(...args: unknown[]) {
  if (IS_DEV) {
    console.log('[v2]', ...args)
  }
}

async function startImportWithRetry(fileId: string, templateId?: string, validFrom?: string | null, validTo?: string | null) {
  try {
    return await bulkTimetableImportApi.startImport({ 
      uploaded_file_id: fileId,
      template_id: templateId,
      valid_from: validFrom,
      valid_to: validTo,
    })
  } catch (err: any) {
    if (err.response?.status === 409) {
      return await bulkTimetableImportApi.startImport({ 
        uploaded_file_id: fileId, 
        force_restart: true,
        template_id: templateId,
        valid_from: validFrom,
        valid_to: validTo,
      })
    }
    throw err
  }
}

export function TimetableImportPage({ loadOnly = false }: { loadOnly?: boolean }) {
  const schoolCountry = 'CH'
  const isEventoSupported = EVENTO_SUPPORTED_COUNTRIES.includes(schoolCountry)
  const [searchParams, setSearchParams] = useSearchParams()
  const jobIdFromUrl = searchParams.get('job')
  const tabFromUrl = searchParams.get('tab')
  const subTabFromUrl = searchParams.get('subtab')

  const [activeTab, setActiveTab] = useState<ExtendedTabType>(() => {
    const allowed = loadOnly
      ? ['files', 'spreadsheet', 'entries']
      : ['files', 'templates', 'spreadsheet', 'evento', 'entries']
    if (tabFromUrl && allowed.includes(tabFromUrl)) {
      return tabFromUrl as ExtendedTabType
    }
    return 'files'
  })
  const [eventoSubTab, setEventoSubTab] = useState<string | undefined>(subTabFromUrl || undefined)
  const [eventoSyncRunning, setEventoSyncRunning] = useState(false)
  const [eventoSyncElapsed, setEventoSyncElapsed] = useState(0)
  const [existingFiles, setExistingFiles] = useState<UploadedFileEntry[]>([])
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [templates, setTemplates] = useState<TemplateInfo[]>([])
  const templatesRef = useRef<TemplateInfo[]>([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [fileSearch, setFileSearch] = useState('')
  const [fileStatusFilter, setFileStatusFilter] = useState<FileStatus | 'all'>('all')
  const [filePage, setFilePage] = useState(1)
  const [templateSearch, setTemplateSearch] = useState('')
  const [fileTotal, setFileTotal] = useState(0)
  const pageSize = 50

  // V2 Workflow State
  const [extractionJobId, setExtractionJobId] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [matchedTokens, setMatchedTokens] = useState<TokenMatch[]>([])
  const [unmatchedTokens, setUnmatchedTokens] = useState<UnmatchedToken[]>([])
  const [tokenPositions, setTokenPositions] = useState<Record<string, TokenPositions>>({})
  const [orphanPatterns, setOrphanPatterns] = useState<Array<{ regex: string; field: string; match_mode: string }>>([])
  const orphanPatternsRef = useRef<Array<{ regex: string; field: string; match_mode: string }>>([])
  const [splitTracking, setSplitTracking] = useState<Record<string, string>>({})
  const [showPostUploadDialog, setShowPostUploadDialog] = useState(false)
  const [showTemplateSelection, setShowTemplateSelection] = useState(false)
  const [pendingExtractFileId, setPendingExtractFileId] = useState<string | null>(null)
  const [postUploadFileCount, setPostUploadFileCount] = useState(0)
  const [showTokenMatching, setShowTokenMatching] = useState(false)
  const [showTokenOK, setShowTokenOK] = useState(false)
  const [showImportValidityDialog, setShowImportValidityDialog] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [parsedEntries, setParsedEntries] = useState<ReconstructedEntry[]>([])
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null)
  const [totalImported, setTotalImported] = useState(0)
  const [isImporting, setIsImporting] = useState(false)
  const [importCancelable, setImportCancelable] = useState(false)
  const [importCancelling, setImportCancelling] = useState(false)
  const [importProgress, setImportProgress] = useState(0)
  const [importPages, setImportPages] = useState({ done: 0, total: 0 })
  const [importStatus, setImportStatus] = useState('Import läuft…')
  const [batchId, setBatchId] = useState<string | null>(null)
  const [unmatchedHeaderCodes, setUnmatchedHeaderCodes] = useState<string[]>([])
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null)

  // Unmatched teacher code dialog state
  const [showUnmatchedCodeDialog, setShowUnmatchedCodeDialog] = useState(false)
  const [unmatchedCodes, setUnmatchedCodes] = useState<string[]>([])
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null)

  // Bulk import flow state
  const [bulkFlowState, setBulkFlowState] = useState<'idle' | 'progress' | 'resolving'>('idle')
  const loadOnlyRef = useRef(loadOnly)
  loadOnlyRef.current = loadOnly
  const revealTokenMatching = () => {
    if (loadOnlyRef.current) {
      setError('Offene Tokens. Die Zuordnung macht der Superadmin beim Import.')
      setBulkFlowState('idle')
      setShowTokenMatching(false)
      return
    }
    setShowTokenMatching(true)
  }
  const [bulkJobId, setBulkJobId] = useState<string | null>(null)
  const [bulkBatchId, setBulkBatchId] = useState<string | null>(null)
  const [bulkShopBatchId, setBulkShopBatchId] = useState<string | null>(null)

  // NEW: Bulk import result view state
  const [showBulkResult, setShowBulkResult] = useState(false)
  const [bulkResultSections, setBulkResultSections] = useState<any[]>([])
  const [bulkPreviewEntries, setBulkPreviewEntries] = useState<Record<string, any[]>>({})

  // Teacher assignment dialog for pending_assignment status
  const [showTeacherAssignmentDialog, setShowTeacherAssignmentDialog] = useState(false)
  const [pendingTeachers, setPendingTeachers] = useState<string[]>([])
  const [perTeacherCounts, setPerTeacherCounts] = useState<Record<string, number>>({})
  const [perTeacherSessions, setPerTeacherSessions] = useState<Record<string, { session_id: string; section_pages: number[]; section_identifier: string | null }>>({})
  
  // NEW: Auto-import in progress flag to hide BatchStatusView
  const [autoImportInProgress, setAutoImportInProgress] = useState(false)
  const autoImportInProgressRef = useRef(false)
  const importAbortRef = useRef<AbortController | null>(null)
  const beginImportRun = useCallback((cancelable: boolean) => {
    importAbortRef.current?.abort()
    const controller = new AbortController()
    importAbortRef.current = controller
    setImportCancelable(cancelable)
    setImportCancelling(false)
    return controller
  }, [])
  const finishImportRun = useCallback((controller: AbortController) => {
    if (importAbortRef.current !== controller) return
    importAbortRef.current = null
    autoImportInProgressRef.current = false
    setAutoImportInProgress(false)
    setIsImporting(false)
    setImportCancelable(false)
    setImportCancelling(false)
  }, [])
  const cancelImportRun = useCallback(() => {
    const controller = importAbortRef.current
    if (!controller || controller.signal.aborted) return
    setImportCancelling(true)
    setImportStatus('Wird abgebrochen…')
    controller.abort()
  }, [])
  const openShopOrImportRef = useRef<(id: string) => void>(() => {})
  const shopImportRetryRef = useRef<string | null>(null)

  // Refresh key to force TimetablesTab reload after import
  const [timetablesRefreshKey, setTimetablesRefreshKey] = useState(0)
  const triggerTimetablesReload = useCallback(() => {
    setTimetablesRefreshKey(prev => prev + 1)
  }, [])

  // Bulk template dialog state (single object to prevent closure bugs)
  interface BulkTemplateDialogState {
    isOpen: boolean
    fileId: string | null
    fileName: string
    pageCount: number
    recommendedTemplateId: string | null
  }
  const [bulkDialogState, setBulkDialogState] = useState<BulkTemplateDialogState>({
    isOpen: false,
    fileId: null,
    fileName: '',
    pageCount: 0,
    recommendedTemplateId: null,
  })
  const bulkDialogStateRef = useRef(bulkDialogState)
  useEffect(() => {
    bulkDialogStateRef.current = bulkDialogState
  }, [bulkDialogState])

  // NEW: Batch status cache for header context
  const [currentBatchStatus, setCurrentBatchStatus] = useState<{
    batch_id: string
    status: string
    total_sections: number
    resolved_sections: number
    imported_sections: number
    failed_sections: number
    sections: Array<{
      section_index: number
      section_identifier: string | null
      session_id: string | null
      status: string
      matched_count: number | null
      unmatched_count: number | null
      has_unmatched: boolean | null
    }>
    created_at: string | null
    completed_at: string | null
  } | null>(null)

  // NEW: Token matching batch info for header display
  const [tokenMatchingBatchInfo, setTokenMatchingBatchInfo] = useState<BatchInfo | undefined>(undefined)

  // Batch persistence for recovery
  const { addBatch, updateBatch, removeBatch, getActiveBatches } = usePersistedBatch()

  // Conflict dialog state
  const [showConflictDialog, setShowConflictDialog] = useState(false)
  const [conflictExistingBatch, setConflictExistingBatch] = useState<PersistedBatchState | null>(null)
  const [conflictNewFileId, setConflictNewFileId] = useState<string | null>(null)
  const [conflictNewFileName, setConflictNewFileName] = useState<string>('')

  const loadExistingFiles = useCallback(async () => {
    setFilesLoading(true)
    try {
      const files = await handleApiRequest<{ files: Array<{
        id: string; original_filename: string; file_size: number; uploaded_at: string;
        is_parsed: boolean; parsing_confidence: number | null; is_validated: boolean;
      }>; total: number }>(apiClient.get('/api/v1/files', {
        params: { file_category: 'timetable', page: filePage, page_size: pageSize },
      }))
      setFileTotal(files.total)
      setExistingFiles(prev => {
        const existing = new Map(prev.map(f => [f.id, f]))
        const newFiles: UploadedFileEntry[] = []
        for (const rf of files.files) {
          const prevFile = existing.get(rf.id)
          newFiles.push({
            id: rf.id,
            original_filename: rf.original_filename,
            file_size: rf.file_size,
            uploaded_at: rf.uploaded_at,
            is_parsed: rf.is_parsed,
            parsing_confidence: rf.parsing_confidence,
            is_validated: rf.is_validated,
            file_category: 'timetable',
            status: rf.is_validated ? 'structure_ok' : rf.is_parsed ? 'structure_ok' : 'idle',
            structure: prevFile?.structure ?? null,
            entries: prevFile?.entries ?? [],
            session: prevFile?.session ?? null,
            error: prevFile?.error ?? null,
          })
        }
        return newFiles.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))
      })
    } catch { /* ignore */ } finally {
      setFilesLoading(false)
    }
  }, [filePage])

  // NEW: Fetch and cache batch status
  const fetchBatchStatus = useCallback(async (batchId: string) => {
    try {
      const status = await timetableImportV2Api.getBatchStatus(batchId)
      setCurrentBatchStatus(status)
      return status
    } catch (err) {
      console.error('Failed to fetch batch status:', err)
      return null
    }
  }, [])

  const reportImportProgress = useCallback((update: { progress: number; message: string; pagesDone?: number; pagesTotal?: number }) => {
    setImportProgress(update.progress || 0)
    setImportPages({ done: update.pagesDone ?? 0, total: update.pagesTotal ?? 0 })
    setImportStatus(importRunLabel(update.message))
  }, [])

  // Auto-import handler for resolved batches
  const handleAutoImport = useCallback(async (batchIdToImport: string) => {
    // Guard against concurrent calls - use ref for synchronous check
    if (autoImportInProgressRef.current && importAbortRef.current && !importAbortRef.current.signal.aborted) {
      return
    }
    autoImportInProgressRef.current = false
    setAutoImportInProgress(false)
    console.log('[TimetableImportPage] handleAutoImport: starting for batchId =', batchIdToImport)
    const controller = beginImportRun(true)
    autoImportInProgressRef.current = true
    setAutoImportInProgress(true)
    setIsImporting(true)
    setImportProgress(0)
    setImportPages({ done: 0, total: 0 })
    setImportStatus('Import läuft…')
    try {
      const result = await timetableImportV2Api.importAllSections(batchIdToImport, 'resolved_only', reportImportProgress, controller.signal)
      if (controller.signal.aborted) return
      console.log('[TimetableImportPage] handleAutoImport: importAllSections result =', result)

      // Check batch status after import
      const batchStatus = await timetableImportV2Api.getBatchStatus(batchIdToImport)
      if (controller.signal.aborted) return
      console.log('[TimetableImportPage] handleAutoImport: batchStatus =', batchStatus)

      const pendingData = await timetableImportV2Api.getBatchPendingStatus(batchIdToImport)
      if (controller.signal.aborted) return
      const teachers = pendingTeachersForDialog(result.pending_teachers, pendingData)

      if (teachers.length > 0) {
        console.log('[TimetableImportPage] handleAutoImport: has pending teachers or pending_assignment, showing assignment dialog')
        if (bulkBatchId !== batchIdToImport && batchId !== batchIdToImport) {
          if (bulkBatchId) {
            setBulkBatchId(batchIdToImport)
          } else {
            setBatchId(batchIdToImport)
          }
        }
        setPendingTeachers(teachers)
        setPerTeacherCounts(pendingData.per_teacher_counts || {})
        setPerTeacherSessions(pendingData.per_teacher_sessions || {})
        setShowTeacherAssignmentDialog(true)
        if ((result.errors || []).some((item) => item.error) || result.failed_sections) {
          setError(importIssueText(result))
        }
        autoImportInProgressRef.current = false
        setAutoImportInProgress(false)
        return
      }

      const issue = importIssueText(result)
      if (issue) {
        setError(issue)
        return
      }
      shopImportRetryRef.current = null
      console.log('[TimetableImportPage] handleAutoImport: import successful, cleaning up')
      removeBatch(batchIdToImport)
      loadExistingFiles()
      loadTemplates()
      triggerTimetablesReload()
      setActiveTab('timetables')
      setSuccess(`${result.imported_sections} Stundenpläne erfolgreich importiert!`)
      setBatchId(null)
    } catch (err: any) {
      if (controller.signal.aborted || isImportCancelled(err)) return
      console.error('[TimetableImportPage] handleAutoImport: Auto-import failed:', err)
      const detail = err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message
      if (err.response?.status === 409 && String(detail).includes('offene Tokens')) {
        if (shopImportRetryRef.current === batchIdToImport) {
          shopImportRetryRef.current = null
          setError('Shop ist leer, Import blockiert. Bitte PDF erneut importieren.')
          return
        }
        shopImportRetryRef.current = batchIdToImport
        openShopOrImportRef.current(batchIdToImport)
        return
      }
      setError('Import fehlgeschlagen: ' + detail)
    } finally {
      finishImportRun(controller)
    }
  }, [beginImportRun, finishImportRun, removeBatch, loadExistingFiles, triggerTimetablesReload, setActiveTab, setSuccess, setError, setBatchId, reportImportProgress])

  const openShopOrImport = useCallback(async (id: string) => {
    try {
      const data = await bulkTimetableImportApi.getShopItems(id)
      if (visibleShopItems(data.items).length > 0) {
        setBulkShopBatchId(id)
        setBulkFlowState('resolving')
        return
      }
    } catch {
      // Shop leer oder nicht ladbar: Import versuchen
    }
    handleAutoImport(id)
  }, [handleAutoImport, setBulkFlowState])
  openShopOrImportRef.current = openShopOrImport

  // NEW: Handle resumeBatch errors with status recovery
  const handleResumeBatchError = useCallback(async (error: any) => {
    const detail = error.response?.data?.detail || 'Resume fehlgeschlagen'
    setError(`Import fortsetzen fehlgeschlagen: ${detail}`)

    if (bulkBatchId) {
      try {
        const batchStatus = await timetableImportV2Api.getBatchStatus(bulkBatchId)

        switch (batchStatus.status) {
          case 'resolved':
          case 'partially_failed':
            setBatchId(bulkBatchId)
            handleAutoImport(bulkBatchId)
            break
          case 'completed':
            // Already completed - just show success
            setSuccess('Import bereits abgeschlossen')
            setBulkFlowState('idle')
            break

          case 'failed':
            setBulkFlowState('idle')
            setError(`Import fehlgeschlagen: ${batchStatus.error || detail}`)
            break

          case 'extracting':
            // Still extracting, resume progress view
            setBulkFlowState('progress')
            setSuccess('Import läuft weiter...')
            break

          case 'pending_bulk_shop':
            openShopOrImport(bulkBatchId)
            break

          case 'resolving':
            const pendingSection = (batchStatus.sections || []).find(
              (s: any) => s.status === 'pending_resolution' && s.session_id
            )
            if (pendingSection?.session_id) {
              setCurrentBatchStatus(batchStatus)
              setSessionId(pendingSection.session_id)
              setTokenMatchingBatchInfo(tokenPageBatchInfo({
                batchId: bulkBatchId,
                sectionIndex: pendingSection.section_index,
                sectionIdentifier: pendingSection.section_identifier,
                totalSections: batchStatus.total_sections,
                startedAt: batchStatus.created_at,
                resolvedSections: batchStatus.resolved_sections,
              }))

              const extractionData = await timetableImportV2Api.getExtractionResult(pendingSection.session_id)
              setCurrentTemplateId(extractionData.updated_template_id || null)
              setMatchedTokens(extractionData.matched_tokens || [])
              setUnmatchedTokens(extractionData.unmatched_tokens || [])
              setTokenPositions(extractionData.token_positions || {})
              setOrphanPatterns(extractionData.orphan_patterns || [])
              orphanPatternsRef.current = extractionData.orphan_patterns || []
              setSplitTracking(extractionData.split_tracking || {})
              setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])

              const matchedTokensList = extractionData.matched_tokens || []
              const unmatchedTokensList = extractionData.unmatched_tokens || []

              if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
                await handleAutoResolveAndNavigate(pendingSection.session_id, autoResolutionsFromMatched(matchedTokensList))
                break
              }

              revealTokenMatching()
            } else {
              setError(`Keine weiteren Seiten zur Auflösung gefunden: ${detail}`)
            }
            break

          case 'pending_assignment':
            setBatchId(bulkBatchId)
            setBulkResultSections(batchStatus.sections || [])
            setCurrentBatchStatus(batchStatus)
            // Fetch pending teachers data and show assignment dialog
            console.log('[TimetableImportPage] handleResumeBatchError: calling getBatchPendingStatus')
            timetableImportV2Api.getBatchPendingStatus(bulkBatchId).then(pendingData => {
              console.log('[TimetableImportPage] handleResumeBatchError: pendingData =', pendingData)
              setPendingTeachers(pendingData.pending_teachers || [])
              setPerTeacherCounts(pendingData.per_teacher_counts || {})
              setPerTeacherSessions(pendingData.per_teacher_sessions || {})
              setShowTeacherAssignmentDialog(true)
            }).catch(() => {
              setShowTeacherAssignmentDialog(true)
            })
            break

          default:
            setError(`Unerwarteter Batch-Status: ${batchStatus.status}. ${detail}`)
            setBulkFlowState('idle')
        }
      } catch {
        setError(`Batch-Status konnte nicht geladen werden: ${detail}`)
        setBulkFlowState('resolving')
      }
    } else {
      setError(`Import fortsetzen fehlgeschlagen: ${detail}`)
    }
  }, [bulkBatchId, setError, setBulkFlowState, setCurrentBatchStatus, setBatchId, setActiveTab, setSuccess, setSessionId, setShowTokenMatching, setMatchedTokens, setUnmatchedTokens, setTokenPositions, setOrphanPatterns, setShowBulkResult, setBulkResultSections, setBulkPreviewEntries, handleAutoImport, openShopOrImport])

  useEffect(() => { loadExistingFiles() }, [loadExistingFiles])

  // NEW: Fetch batch status when batch ID changes
  useEffect(() => {
    if (bulkBatchId) {
      fetchBatchStatus(bulkBatchId)
    } else {
      setCurrentBatchStatus(null)
    }
  }, [bulkBatchId, fetchBatchStatus])

  useEffect(() => {
    if (!showTokenMatching || !sessionId || !currentBatchStatus) return
    const section = currentBatchStatus.sections.find((row) => row.session_id === sessionId)
    if (!section) return
    const teacher = teacherCodeFromTokens(matchedTokens)
    setTokenMatchingBatchInfo((prev) => {
      const next = tokenPageBatchInfo({
        batchId: currentBatchStatus.batch_id || prev?.batchId || bulkBatchId || '',
        sectionIndex: section.section_index,
        sectionIdentifier: section.section_identifier,
        totalSections: currentBatchStatus.total_sections,
        startedAt: prev?.startedAt || currentBatchStatus.created_at,
        teacherCode: teacher,
        isContinueMode: prev?.isContinueMode ?? true,
        resolvedSections: currentBatchStatus.resolved_sections,
      })
      if (
        prev &&
        prev.batchId === next.batchId &&
        prev.sectionIndex === next.sectionIndex &&
        prev.totalSections === next.totalSections &&
        (prev.sectionIdentifier || null) === (next.sectionIdentifier || null) &&
        prev.resolvedSections === next.resolvedSections
      ) return prev
      return next
    })
  }, [showTokenMatching, sessionId, currentBatchStatus, matchedTokens, bulkBatchId])

  

  // NEW: Sync batch info to URL when modal opens
  useEffect(() => {
    if (showTokenMatching && tokenMatchingBatchInfo) {
      const params = new URLSearchParams(window.location.search)
      params.set('batchId', tokenMatchingBatchInfo.batchId)
      params.set('section', tokenMatchingBatchInfo.sectionIndex.toString())
      window.history.replaceState({}, '', `?${params.toString()}`)
    } else if (!showTokenMatching) {
      // Clear params when modal closes
      const params = new URLSearchParams(window.location.search)
      params.delete('batchId')
      params.delete('section')
      const newParams = params.toString()
      window.history.replaceState({}, '', newParams ? `?${newParams}` : window.location.pathname)
    }
  }, [showTokenMatching, tokenMatchingBatchInfo])

  // NEW: Restore from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlBatchId = params.get('batchId')
    const urlSection = params.get('section')

    if (urlBatchId && urlSection && !showTokenMatching) {
      // Restore batch context and reopen modal
      const sectionIndex = parseInt(urlSection, 10)
      if (!isNaN(sectionIndex)) {
        restoreBatchContext(urlBatchId, sectionIndex)
      }
    }
  }, [])

  useEffect(() => {
    const savedJobId = sessionStorage.getItem('v2_extraction_job_id')
    if (savedJobId) {
      setExtractionJobId(savedJobId)
    }
    const savedSessionId = sessionStorage.getItem('v2_session_id')
    if (savedSessionId) {
      setSessionId(savedSessionId)
    }
    const savedEntries = sessionStorage.getItem('v2_parsed_entries')
    if (savedEntries) {
      try {
        setParsedEntries(JSON.parse(savedEntries))
      } catch {
        sessionStorage.removeItem('v2_parsed_entries')
      }
    }
  }, [])

  useEffect(() => {
    if (extractionJobId) {
      sessionStorage.setItem('v2_extraction_job_id', extractionJobId)
    } else {
      sessionStorage.removeItem('v2_extraction_job_id')
    }
  }, [extractionJobId])

  useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem('v2_session_id', sessionId)
    } else {
      sessionStorage.removeItem('v2_session_id')
    }
  }, [sessionId])

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true)
    try {
      const t = await templateTrainingApi.listTemplates()
      setTemplates(t)
      templatesRef.current = t
      return t
    } catch {
      setTemplates([])
      templatesRef.current = []
      return []
    } finally {
      setTemplatesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTemplates()
  }, [loadTemplates])

  // Fallback: updates when templates state changes from other sources
  // Note: loadTemplates() updates templatesRef.current directly
  useEffect(() => {
    templatesRef.current = templates
  }, [templates])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [success])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [error])

  useEffect(() => {
    if (jobIdFromUrl) {
      templateTrainingApi.getJobStatus(jobIdFromUrl).then(job => {
        if (job.status === 'completed') {
          setSuccess(`${job.result?.entries_count ?? 0} Einträge extrahiert.`)
          setActiveTab('files')
          setSelectedFileId(null)
          setSearchParams({}, { replace: true })
          loadExistingFiles()
        } else if (job.status === 'failed') {
          setError(job.error || job.message || 'Job fehlgeschlagen')
          setSearchParams({}, { replace: true })
        } else if (job.status === 'cancelled') {
          setSearchParams({}, { replace: true })
        }
      }).catch(() => {
        setSearchParams({}, { replace: true })
      })
    }
  }, [jobIdFromUrl])

  const clearV2SessionState = useCallback(() => {
    sessionStorage.removeItem('v2_extraction_job_id')
    sessionStorage.removeItem('v2_session_id')
    sessionStorage.removeItem('v2_parsed_entries')
    setExtractionJobId(null)
    setSessionId(null)
    setParsedEntries([])
    setMatchedTokens([])
    setUnmatchedTokens([])
    setImportResult(null)
    setTotalImported(0)
  }, [])

  // NEW: Restore batch context from URL params
  const restoreBatchContext = useCallback(async (batchId: string, sectionIndex: number) => {
    try {
      const batchStatus = await timetableImportV2Api.getBatchStatus(batchId)
      const section = batchStatus.sections[sectionIndex]

      if (section?.session_id) {
        setTokenMatchingBatchInfo({
          batchId,
          sectionIndex,
          totalSections: batchStatus.total_sections,
          sectionIdentifier: section.section_identifier,
          isContinueMode: true, // Can only restore in continue mode
          startedAt: batchStatus.created_at || new Date().toISOString()
        })

        // Reopen modal
        const extractionData = await timetableImportV2Api.getExtractionResult(section.session_id)
        setCurrentTemplateId(extractionData.updated_template_id || null)
        setMatchedTokens(extractionData.matched_tokens || [])
        setUnmatchedTokens(extractionData.unmatched_tokens || [])
        setTokenPositions(extractionData.token_positions || {})
        setOrphanPatterns(extractionData.orphan_patterns || [])
        setSplitTracking(extractionData.split_tracking || {})
        setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
        setSessionId(section.session_id)

        const matchedTokensList = extractionData.matched_tokens || []
        const unmatchedTokensList = extractionData.unmatched_tokens || []

        if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
          await handleAutoResolveAndNavigate(section.session_id, autoResolutionsFromMatched(matchedTokensList))
          return
        }

        revealTokenMatching()
      }
    } catch (err) {
      console.error('Failed to restore batch context:', err)
      setError('Sitzung konnte nicht wiederhergestellt werden.')
    }
  }, [])

  // NEW: Handle pause from token matching modal
  const handleTokenMatchingPause = useCallback(() => {
    // Save current progress to localStorage for recovery
    if (tokenMatchingBatchInfo) {
      localStorage.setItem('token_matching_paused', JSON.stringify({
        batchInfo: tokenMatchingBatchInfo,
        timestamp: new Date().toISOString(),
      }))
    }

    // Persist batch state to main storage for recovery via BatchRecoveryBanner
    if (bulkBatchId && sessionId) {
      updateBatch(bulkBatchId, {
        status: 'resolving',
        unresolvedSessionId: sessionId,
      })
    }

    setShowTokenMatching(false)
    setSuccess('Token-Zuordnung pausiert. Klicke "Continue" um fortzufahren.')
  }, [tokenMatchingBatchInfo, bulkBatchId, sessionId, updateBatch])

  // NEW: Handle cancel with batch info clearing
  const handleTokenMatchingCancel = useCallback(() => {
    // Persist batch state to localStorage before clearing React state
    if (bulkBatchId && sessionId) {
      updateBatch(bulkBatchId, {
        status: 'resolving',
        unresolvedSessionId: sessionId,
      })
    }

    setShowTokenMatching(false)
    setTokenMatchingBatchInfo(undefined)
    // Return to resolving state if we're in bulk import flow
    if (bulkBatchId) {
      setBulkFlowState('resolving')
    }
  }, [bulkBatchId, sessionId, updateBatch])

  const handleFileUpload = useCallback(async (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!
      const tempId = `temp-${Date.now()}-${i}`
      setExistingFiles(prev => [{
        id: tempId, original_filename: file.name, file_size: file.size,
        uploaded_at: new Date().toISOString(),
        is_parsed: false, parsing_confidence: null, is_validated: false,
        file_category: 'timetable',
        status: 'uploading', structure: null, entries: [], session: null, error: null,
      }, ...prev])

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('file_category', 'timetable')
        const uploadResult = await handleApiRequest<{ id: string }>(apiClient.post('/api/v1/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }))
        await loadExistingFiles()
        setPendingExtractFileId(uploadResult.id)
        setPostUploadFileCount(c => c + 1)

        if (extractionJobId) {
          debugLog('Extraction already in progress, ignoring upload')
          return
        }

        // Check for existing pending batch with same filename
        const pendingBatches = getActiveBatches()
        const existingBatch = pendingBatches.find(b => b.fileName === file.name && b.status !== 'completed')
        
        if (existingBatch) {
          // Show conflict dialog
          setConflictExistingBatch(existingBatch)
          setConflictNewFileId(uploadResult.id)
          setConflictNewFileName(file.name)
          setShowConflictDialog(true)
          return
        }

        clearV2SessionState()

        try {
          const detect = await bulkTimetableImportApi.detect(uploadResult.id)
          if (detect.use_bulk_import) {
            await startBulkImportWithDialog(uploadResult.id, file.name, detect.page_count || 0)
          } else {
            setShowTemplateSelection(true)
            setPendingExtractFileId(uploadResult.id)
          }
        } catch {
          setError('Fehler beim Erkennen des Dateityps')
        }
        setSuccess(`"${file.name}" hochgeladen.`)
      } catch (err: any) {
        const msg = err.response?.data?.detail?.message || err.response?.data?.detail || 'Upload fehlgeschlagen'
        const errorMsg = typeof msg === 'object' ? JSON.stringify(msg) : msg
        setExistingFiles(prev => prev.map(f => f.id === tempId ? { ...f, status: 'error', error: errorMsg } : f))
        await loadExistingFiles()
        setExistingFiles(prev => prev.map(f =>
          f.original_filename === file.name && (f.status === 'idle' || f.status === 'extracting')
            ? { ...f, status: 'error', error: errorMsg }
            : f
        ))
      }
    }
  }, [loadExistingFiles, clearV2SessionState, extractionJobId, getActiveBatches, addBatch])

  const handleDeleteFile = useCallback(async (fileId: string) => {
    try {
      await handleApiRequest(apiClient.delete(`/api/v1/files/${fileId}`))
      if (selectedFileId === fileId) {
        setSelectedFileId(null)
      }
      await loadExistingFiles()
      setSuccess('Datei gelöscht.')
    } catch (err: any) {
      const detail = err.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : detail?.message || detail?.msg || 'Löschung fehlgeschlagen'
      setError(msg)
    }
  }, [selectedFileId, loadExistingFiles])

  const startBulkImportWithDialog = useCallback(async (
    fileId: string,
    fileName: string,
    pageCount: number
  ) => {
    let recommendedId: string | null = null
    try {
      const templateInfo = await bulkTimetableImportApi.getTemplateForFile(fileId)
      if (templateInfo.template_id && templateInfo.confirmed_token_count > 0) {
        recommendedId = templateInfo.template_id
      }
    } catch {
    }

    setBulkDialogState({
      isOpen: true,
      fileId,
      fileName,
      pageCount,
      recommendedTemplateId: recommendedId,
    })
  }, [])

  const handleBulkDialogResult = useCallback(async (
    result: { type: 'start'; templateId: string | null; validFrom?: string | null; validTo?: string | null } | { type: 'cancelled' }
  ) => {
    const currentState = bulkDialogStateRef.current
    
    if (result.type === 'cancelled') {
      setBulkDialogState({
        isOpen: false,
        fileId: null,
        fileName: '',
        pageCount: 0,
        recommendedTemplateId: null,
      })
      return
    }

    const { fileId, fileName, pageCount } = currentState
    
    if (!fileId) {
      setBulkDialogState(prev => ({ ...prev, isOpen: false }))
      return
    }

    setBulkDialogState(prev => ({ ...prev, isOpen: false }))

    try {
      const importResult = await startImportWithRetry(fileId, result.templateId || undefined, result.validFrom, result.validTo)
      setBulkJobId(importResult.job_id)
      setBulkBatchId(importResult.batch_id)
      setBulkFlowState('progress')
      
      addBatch({
        batchId: importResult.batch_id,
        jobId: importResult.job_id,
        fileId,
        fileName,
        startedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        status: 'extracting',
        totalSections: pageCount,
        resolvedSections: 0,
        unresolvedSessionId: null,
      })
    } catch (err: any) {
      setError('Fehler beim Starten des Imports: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
    }
  }, [addBatch])

  const handleBulkDelete = useCallback(async (fileIds: string[]) => {
    const succeeded: string[] = []
    const errors: string[] = []
    for (const fileId of fileIds) {
      try {
        await handleApiRequest(apiClient.delete(`/api/v1/files/${fileId}`))
        succeeded.push(fileId)
      } catch {
        const file = existingFiles.find(f => f.id === fileId)
        errors.push(file?.original_filename || 'Unbekannte Datei')
      }
    }
    if (selectedFileId && fileIds.includes(selectedFileId)) {
      setSelectedFileId(null)
    }
    await loadExistingFiles()
    if (errors.length === 0) {
      setSuccess(`${fileIds.length} Dateien gelöscht.`)
    } else if (succeeded.length === 0) {
      setError('Keine Dateien konnten gelöscht werden')
    } else {
      setSuccess(`${succeeded.length} von ${fileIds.length} Dateien gelöscht.`)
      setError(`${errors.length} fehlgeschlagen: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`)
    }
  }, [selectedFileId, loadExistingFiles, existingFiles])

  const handleBulkExtract = useCallback(async (fileIds: string[]) => {
    try {
      if (fileIds.length > 1) {
        setSuccess(`${fileIds.length} Dateien ausgewählt. Verarbeite die erste Datei, die restlichen werden ignoriert.`)
      }
      const fileId = fileIds[0]
      if (!fileId) return

      clearV2SessionState()
      
      const detect = await bulkTimetableImportApi.detect(fileId)
      if (detect.use_bulk_import) {
        const fileInfo = existingFiles.find(f => f.id === fileId)
        const name = fileInfo?.original_filename || ''
        await startBulkImportWithDialog(fileId, name, detect.page_count || 0)
      } else {
        setPendingExtractFileId(fileId)
        setShowTemplateSelection(true)
      }
    } catch {
      setError('Fehler beim Erkennen des Dateityps')
    }
  }, [clearV2SessionState, existingFiles, startBulkImportWithDialog])

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await templateTrainingApi.deleteTemplate(templateId)
      setTemplates(prev => {
        const updated = prev.filter(t => t.id !== templateId)
        templatesRef.current = updated
        return updated
      })
      setSuccess('Template gelöscht.')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Template-Löschung fehlgeschlagen')
    }
  }, [])

  const handleDeleteAllTemplates = useCallback(async () => {
    try {
      const result = await templateTrainingApi.deleteAllTemplates()
      clearPersistedBatches()
      setTemplates([])
      templatesRef.current = []
      setSuccess(result.message || 'Alle Templates gelöscht.')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Löschen aller Templates fehlgeschlagen')
    }
  }, [])

  const handleV2Extract = useCallback(async (fileId: string, templateId?: string) => {
    if (!fileId) return
    debugLog('Starting extraction - fileId:', fileId, 'templateId:', templateId)
    try {
      setShowTemplateSelection(false)
      const result = await timetableImportV2Api.extract(fileId, templateId)
      debugLog('Extraction job started - jobId:', result.job_id)
      setExtractionJobId(result.job_id)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      if (detail === 'Template not found' && templateId) {
        debugLog('Template deleted, retrying without template')
        setError('Template wurde gelöscht. Extraktion ohne Template gestartet.')
        const retryResult = await timetableImportV2Api.extract(fileId)
        setExtractionJobId(retryResult.job_id)
      } else {
        debugLog('Extraction failed:', err)
        setError('Extraktion fehlgeschlagen: ' + (detail || err.message))
        setShowTemplateSelection(false)
        setPendingExtractFileId(null)
      }
    }
  }, [])

  const handleContinueBatch = useCallback(async (batch: PersistedBatchState) => {
    try {
      const batchStatus = await timetableImportV2Api.getBatchStatus(batch.batchId)
      setCurrentBatchStatus(batchStatus)

      setBulkBatchId(batch.batchId)
      setBulkJobId(batch.jobId)

      const action = nextContinueAction(
        batchStatus.status,
        batchStatus.sections || [],
        Boolean(batch.jobId),
      )

      if (action.type === 'token_matching') {
        localStorage.removeItem('token_matching_paused')
        setTokenMatchingBatchInfo({
          batchId: batch.batchId,
          sectionIndex: action.sectionIndex,
          totalSections: batch.totalSections,
          sectionIdentifier: action.sectionIdentifier,
          isContinueMode: true,
          startedAt: batch.startedAt,
        })
        setSessionId(action.sessionId)
        setBulkFlowState('resolving')

        const extractionData = await timetableImportV2Api.getExtractionResult(action.sessionId)
        setCurrentTemplateId(extractionData.updated_template_id || null)
        setMatchedTokens(extractionData.matched_tokens || [])
        setUnmatchedTokens(extractionData.unmatched_tokens || [])
        setTokenPositions(extractionData.token_positions || {})
        setOrphanPatterns(extractionData.orphan_patterns || [])
        orphanPatternsRef.current = extractionData.orphan_patterns || []
        setSplitTracking(extractionData.split_tracking || {})
        setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
        if (shouldSkipTokenMatching(
          extractionData.matched_tokens || [],
          extractionData.unmatched_tokens || [],
          extractionData.unmatched_header_codes || [],
        )) {
          await handleAutoResolveAndNavigate(action.sessionId, autoResolutionsFromMatched(extractionData.matched_tokens || []))
          return
        }
        revealTokenMatching()
        updateBatch(batch.batchId, {
          unresolvedSessionId: action.sessionId,
          status: 'resolving',
        })
        return
      }

      if (action.type === 'shop') {
        updateBatch(batch.batchId, { status: 'pending_bulk_shop' })
        openShopOrImport(batch.batchId)
        return
      }

      if (action.type === 'assignment') {
        autoImportInProgressRef.current = true
        setAutoImportInProgress(true)
        setBatchId(batch.batchId)
        setBulkResultSections(batchStatus.sections || [])
        try {
          const pendingData = await timetableImportV2Api.getBatchPendingStatus(batch.batchId)
          setPendingTeachers(pendingData.pending_teachers || [])
          setPerTeacherCounts(pendingData.per_teacher_counts || {})
          setPerTeacherSessions(pendingData.per_teacher_sessions || {})
        } catch {
          // still show the dialog
        }
        setShowTeacherAssignmentDialog(true)
        autoImportInProgressRef.current = false
        setAutoImportInProgress(false)
        return
      }

      if (action.type === 'import') {
        setBatchId(batch.batchId)
        setBulkFlowState('idle')
        handleAutoImport(batch.batchId)
        return
      }

      if (action.type === 'progress') {
        let jobId = batch.jobId
        const dead = batchStatus.status === 'failed' || batchStatus.status === 'cancelled'
        if (dead) jobId = null
        if (jobId) {
          try {
            const job = await timetableImportV2Api.getJobStatus(jobId)
            if (job.status !== 'pending' && job.status !== 'running') jobId = null
          } catch {
            jobId = null
          }
        }
        if (!jobId) {
          const resumed = await bulkTimetableImportApi.resumeBatch(batch.batchId)
          if (resumed.job_id) {
            jobId = resumed.job_id
            updateBatch(batch.batchId, { jobId, status: 'extracting' })
          } else if (resumed.batch_status === 'pending_bulk_shop') {
            openShopOrImport(batch.batchId)
            return
          } else if (resumed.batch_status === 'resolved' || resumed.batch_status === 'partially_failed') {
            handleAutoImport(batch.batchId)
            return
          } else if (resumed.batch_status === 'pending_assignment') {
            try {
              const pendingData = await timetableImportV2Api.getBatchPendingStatus(batch.batchId)
              setPendingTeachers(pendingData.pending_teachers || [])
              setPerTeacherCounts(pendingData.per_teacher_counts || {})
              setPerTeacherSessions(pendingData.per_teacher_sessions || {})
            } catch {
              /* dialog still opens */
            }
            setShowTeacherAssignmentDialog(true)
            return
          }
        }
        if (jobId) {
          setBulkJobId(jobId)
          setBulkFlowState('progress')
        } else {
          const after = await timetableImportV2Api.getBatchStatus(batch.batchId)
          const follow = nextContinueAction(after.status, after.sections || [], false)
          if (follow.type === 'shop') {
            openShopOrImport(batch.batchId)
          } else if (follow.type === 'import') {
            handleAutoImport(batch.batchId)
          } else if (follow.type === 'token_matching') {
            setCurrentBatchStatus(after)
            setSessionId(follow.sessionId)
            setTokenMatchingBatchInfo(tokenPageBatchInfo({
              batchId: batch.batchId,
              sectionIndex: follow.sectionIndex,
              sectionIdentifier: follow.sectionIdentifier,
              totalSections: after.total_sections,
              startedAt: batch.startedAt,
              resolvedSections: after.resolved_sections,
            }))
            setBulkFlowState('resolving')
            const extractionData = await timetableImportV2Api.getExtractionResult(follow.sessionId)
            setCurrentTemplateId(extractionData.updated_template_id || null)
            setMatchedTokens(extractionData.matched_tokens || [])
            setUnmatchedTokens(extractionData.unmatched_tokens || [])
            setTokenPositions(extractionData.token_positions || {})
            setOrphanPatterns(extractionData.orphan_patterns || [])
            orphanPatternsRef.current = extractionData.orphan_patterns || []
            setSplitTracking(extractionData.split_tracking || {})
            setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
            if (shouldSkipTokenMatching(
              extractionData.matched_tokens || [],
              extractionData.unmatched_tokens || [],
              extractionData.unmatched_header_codes || [],
            )) {
              await handleAutoResolveAndNavigate(
                follow.sessionId,
                autoResolutionsFromMatched(extractionData.matched_tokens || []),
              )
            } else {
              revealTokenMatching()
            }
          } else {
            setBulkFlowState('idle')
          }
        }
        return
      }

      if (action.type === 'completed') {
        removeBatch(batch.batchId)
        setSuccess('Import bereits abgeschlossen')
        return
      }

      setError(action.message)
    } catch (err: any) {
      if (err.response?.status === 404) {
        removeBatch(batch.batchId)
        setError('Dieser Import wurde abgebrochen oder gelöscht. Bitte das PDF erneut hochladen.')
        return
      }
      setError('Import fortsetzen fehlgeschlagen: ' + (err.response?.data?.detail || err.message))
    }
  }, [updateBatch, removeBatch, handleAutoImport, openShopOrImport])

  useEffect(() => {
    const id = searchParams.get('continueBatch')
    if (!id) return
    const next = new URLSearchParams(searchParams)
    next.delete('continueBatch')
    setSearchParams(next, { replace: true })
    void handleContinueBatch({
      batchId: id,
      jobId: null,
      fileId: '',
      fileName: 'Import',
      startedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      status: 'extracting',
      totalSections: 0,
      resolvedSections: 0,
      unresolvedSessionId: null,
    })
  }, [searchParams, setSearchParams, handleContinueBatch])

  // Conflict dialog handlers
  const handleConflictContinue = useCallback(async () => {
    setShowConflictDialog(false)
    if (conflictExistingBatch) {
      await handleContinueBatch(conflictExistingBatch)
    }
  }, [conflictExistingBatch, handleContinueBatch])

  const handleConflictRestart = useCallback(async () => {
    setShowConflictDialog(false)
    if (conflictExistingBatch) {
      removeBatch(conflictExistingBatch.batchId)
      try {
        await bulkTimetableImportApi.cleanupBatch(conflictExistingBatch.batchId)
      } catch {
        // Cleanup may fail - ignore
      }
    }
    if (conflictNewFileId) {
      clearV2SessionState()
      try {
        const detect = await bulkTimetableImportApi.detect(conflictNewFileId)
        if (detect.use_bulk_import) {
          await startBulkImportWithDialog(conflictNewFileId, conflictNewFileName, detect.page_count || 0)
        }
      } catch {
        setError('Fehler beim Starten des Imports')
      }
    }
    setConflictExistingBatch(null)
    setConflictNewFileId(null)
    setConflictNewFileName('')
  }, [conflictExistingBatch, conflictNewFileId, conflictNewFileName, removeBatch, clearV2SessionState, addBatch])

  const handleConflictCancel = useCallback(() => {
    setShowConflictDialog(false)
    setConflictExistingBatch(null)
    setConflictNewFileId(null)
    setConflictNewFileName('')
  }, [])

  const handleAutoResolveAndNavigate = useCallback(async (sid: string, autoResolutions: Array<{ token: string; field: string; confirmed: boolean }>) => {
    const currentOrphanPatterns = orphanPatternsRef.current
    try {
      const result = await timetableImportV2Api.resolveTokens(sid, autoResolutions, currentOrphanPatterns.length > 0 ? currentOrphanPatterns : undefined)
      if (result.reconstructed_entries && result.reconstructed_entries.length > 0) {
        setParsedEntries(result.reconstructed_entries)
        sessionStorage.setItem('v2_parsed_entries', JSON.stringify(result.reconstructed_entries))
      }
      if (result.updated_template_id) {
        await loadTemplates()
        const newTemplateId = result.updated_template_id
        if (newTemplateId) {
          setCurrentTemplateId(newTemplateId)
        }
        setSuccess('Template erstellt und Token-Zuordnung gespeichert.')
      }
    } catch (err: any) {
      setError('Token-Zuordnung fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
      return
    }
    // In bulk import flow, check batch status and handle appropriately
    if (bulkBatchId) {
      try {
        const batchStatus = await timetableImportV2Api.getBatchStatus(bulkBatchId)
        setCurrentBatchStatus(batchStatus)

        const postTemplateAction = nextContinueAction(
          batchStatus.status,
          batchStatus.sections || [],
          Boolean(bulkJobId),
        )
        if (postTemplateAction.type === 'shop') {
          openShopOrImport(bulkBatchId)
        } else if (postTemplateAction.type === 'import') {
          handleAutoImport(bulkBatchId)
        } else if (postTemplateAction.type === 'token_matching') {
          setCurrentBatchStatus(batchStatus)
          setSessionId(postTemplateAction.sessionId)
          setTokenMatchingBatchInfo(tokenPageBatchInfo({
            batchId: bulkBatchId,
            sectionIndex: postTemplateAction.sectionIndex,
            sectionIdentifier: postTemplateAction.sectionIdentifier,
            totalSections: batchStatus.total_sections,
            startedAt: batchStatus.created_at,
            resolvedSections: batchStatus.resolved_sections,
          }))
          setBulkFlowState('resolving')
          const extractionData = await timetableImportV2Api.getExtractionResult(postTemplateAction.sessionId)
          setCurrentTemplateId(extractionData.updated_template_id || null)
          setMatchedTokens(extractionData.matched_tokens || [])
          setUnmatchedTokens(extractionData.unmatched_tokens || [])
          setTokenPositions(extractionData.token_positions || {})
          setOrphanPatterns(extractionData.orphan_patterns || [])
          orphanPatternsRef.current = extractionData.orphan_patterns || []
          setSplitTracking(extractionData.split_tracking || {})
          setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
          if (shouldSkipTokenMatching(
            extractionData.matched_tokens || [],
            extractionData.unmatched_tokens || [],
            extractionData.unmatched_header_codes || [],
          )) {
            await handleAutoResolveAndNavigate(
              postTemplateAction.sessionId,
              autoResolutionsFromMatched(extractionData.matched_tokens || []),
            )
          } else if (!loadOnly) {
            revealTokenMatching()
          } else {
            setError('Offene Tokens. Die Zuordnung macht der Superadmin beim Import.')
            setBulkFlowState('idle')
          }
        } else if (batchStatus.status === 'resolving') {
          const resumeResult = await bulkTimetableImportApi.resumeBatch(bulkBatchId)
          if (resumeResult.batch_status === 'pending_bulk_shop') {
            openShopOrImport(bulkBatchId)
          } else if (resumeResult.batch_status === 'resolved' || resumeResult.batch_status === 'partially_failed') {
            handleAutoImport(bulkBatchId)
          } else if (resumeResult.job_id && resumeResult.batch_status !== 'resolving') {
            setBulkJobId(resumeResult.job_id)
            setBulkFlowState('progress')
          } else {
            const after = await timetableImportV2Api.getBatchStatus(bulkBatchId)
            const pending = (after.sections || []).find(
              (s) => s.status === 'pending_resolution' && s.session_id,
            )
            if (pending?.session_id) {
              setCurrentBatchStatus(after)
              setSessionId(pending.session_id)
              setTokenMatchingBatchInfo(tokenPageBatchInfo({
                batchId: bulkBatchId,
                sectionIndex: pending.section_index,
                sectionIdentifier: pending.section_identifier,
                totalSections: after.total_sections,
                startedAt: batchStatus.created_at,
                teacherCode: undefined,
                resolvedSections: after.resolved_sections,
              }))
              setBulkFlowState('resolving')
              const extractionData = await timetableImportV2Api.getExtractionResult(pending.session_id)
              setCurrentTemplateId(extractionData.updated_template_id || null)
              setMatchedTokens(extractionData.matched_tokens || [])
              setUnmatchedTokens(extractionData.unmatched_tokens || [])
              setTokenPositions(extractionData.token_positions || {})
              setOrphanPatterns(extractionData.orphan_patterns || [])
              orphanPatternsRef.current = extractionData.orphan_patterns || []
              setSplitTracking(extractionData.split_tracking || {})
              setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
              if (
                pending.session_id !== sid &&
                shouldSkipTokenMatching(
                  extractionData.matched_tokens || [],
                  extractionData.unmatched_tokens || [],
                  extractionData.unmatched_header_codes || [],
                )
              ) {
                await handleAutoResolveAndNavigate(
                  pending.session_id,
                  autoResolutionsFromMatched(extractionData.matched_tokens || []),
                )
              } else if (shouldSkipTokenMatching(
                extractionData.matched_tokens || [],
                extractionData.unmatched_tokens || [],
                extractionData.unmatched_header_codes || [],
              )) {
                openShopOrImport(bulkBatchId)
              } else if (!loadOnly) {
                revealTokenMatching()
              } else {
                setError('Offene Tokens. Die Zuordnung macht der Superadmin beim Import.')
                setBulkFlowState('idle')
              }
            } else if (after.status === 'pending_bulk_shop') {
              openShopOrImport(bulkBatchId)
            } else {
              setBulkFlowState('idle')
            }
          }
        } else if (batchStatus.status === 'resolved' || batchStatus.status === 'partially_failed') {
          handleAutoImport(bulkBatchId)
        } else if (batchStatus.status === 'completed') {
          setSuccess('Import bereits abgeschlossen')
          setBulkFlowState('idle')
        } else if (batchStatus.status === 'pending_assignment') {
          const pendingData = await timetableImportV2Api.getBatchPendingStatus(bulkBatchId)
          setPendingTeachers(pendingData.pending_teachers || [])
          setPerTeacherCounts(pendingData.per_teacher_counts || {})
          setPerTeacherSessions(pendingData.per_teacher_sessions || {})
          setShowTeacherAssignmentDialog(true)
        } else {
          setBulkFlowState('progress')
        }
      } catch (err: any) {
        await handleResumeBatchError(err)
      }
    } else {
      setActiveTab('entries')
      setShowTokenOK(true)
    }
  }, [loadOnly, loadTemplates, bulkBatchId, handleResumeBatchError, handleAutoImport, openShopOrImport, setCurrentBatchStatus, setPendingTeachers, setPerTeacherCounts, setPerTeacherSessions, setShowTeacherAssignmentDialog, setBulkFlowState, setBulkJobId])

  const handleResolveTokens = useCallback(async (resolutions: TokenResolution[], resolutionContexts?: TokenResolutionContext[]) => {
    if (!sessionId) return
    setIsResolving(true)
    try {
      // Debug: log what's being sent (always log, not just in DEV)
      console.log('[TimetableImportPage] handleResolveTokens:', {
        sessionId,
        resolutionsCount: resolutions.length,
        confirmedCount: resolutions.filter(r => r.confirmed).length,
        splitCount: resolutions.filter(r => r.split).length,
        contextsCount: resolutionContexts?.length || 0,
      })
      
      const currentOrphanPatterns = orphanPatternsRef.current
      const result = await timetableImportV2Api.resolveTokens(sessionId, resolutions, currentOrphanPatterns.length > 0 ? currentOrphanPatterns : undefined, resolutionContexts)
      
      // Handle invalid teacher code errors
      if (result.error && (result.entries_with_invalid_teacher || result.error.includes('Lehrercode'))) {
        setIsResolving(false)
        setError(result.error + ' Bitte überprüfen Sie die Lehrercodes und weisen Sie sie manuell zu.')
        // Refresh session to get updated status
        if (sessionId) {
          try {
            await timetableImportV2Api.getSession(sessionId)
          } catch (err) {
            console.error('Failed to refresh session:', err)
          }
        }
        return
      }
      
      setIsResolving(false)
      setShowTokenMatching(false)
      
      if (result.reconstructed_entries && result.reconstructed_entries.length > 0) {
        setParsedEntries(result.reconstructed_entries)
        sessionStorage.setItem('v2_parsed_entries', JSON.stringify(result.reconstructed_entries))
      }
      if (result.updated_template_id) {
        await loadTemplates()
        const newTemplateId = result.updated_template_id
        if (newTemplateId) {
          setCurrentTemplateId(newTemplateId)
        }
        setSuccess('Template erstellt und Token-Zuordnung gespeichert.')
      }
      
      // In bulk import flow, resume extraction for next page
      if (bulkBatchId) {
        try {
          // Check batch status before trying to resume
          const batchStatus = await timetableImportV2Api.getBatchStatus(bulkBatchId)
          console.log('[TimetableImportPage] handleResolveTokens: batchStatus =', batchStatus)
          setCurrentBatchStatus(batchStatus)

          // Only call resumeBatch if batch is in 'resolving' status with paused_at_section
          const resolveAction = nextContinueAction(
            batchStatus.status,
            batchStatus.sections || [],
            Boolean(bulkJobId),
          )
          if (resolveAction.type === 'shop') {
            openShopOrImport(bulkBatchId)
          } else if (resolveAction.type === 'import') {
            handleAutoImport(bulkBatchId)
          } else if (resolveAction.type === 'token_matching') {
            const pending = (batchStatus.sections || []).find(
              (s) => s.session_id === resolveAction.sessionId,
            )
            setCurrentBatchStatus(batchStatus)
            setSessionId(resolveAction.sessionId)
            setTokenMatchingBatchInfo(tokenPageBatchInfo({
              batchId: bulkBatchId,
              sectionIndex: pending?.section_index ?? resolveAction.sectionIndex,
              sectionIdentifier: pending?.section_identifier ?? resolveAction.sectionIdentifier,
              totalSections: batchStatus.total_sections,
              startedAt: batchStatus.created_at,
              resolvedSections: batchStatus.resolved_sections,
            }))
            setBulkFlowState('resolving')
            const extractionData = await timetableImportV2Api.getExtractionResult(resolveAction.sessionId)
            setCurrentTemplateId(extractionData.updated_template_id || null)
            setMatchedTokens(extractionData.matched_tokens || [])
            setUnmatchedTokens(extractionData.unmatched_tokens || [])
            setTokenPositions(extractionData.token_positions || {})
            setOrphanPatterns(extractionData.orphan_patterns || [])
            orphanPatternsRef.current = extractionData.orphan_patterns || []
            setSplitTracking(extractionData.split_tracking || {})
            setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
            if (shouldSkipTokenMatching(
              extractionData.matched_tokens || [],
              extractionData.unmatched_tokens || [],
              extractionData.unmatched_header_codes || [],
            )) {
              await handleAutoResolveAndNavigate(
                resolveAction.sessionId,
                autoResolutionsFromMatched(extractionData.matched_tokens || []),
              )
            } else {
              revealTokenMatching()
            }
          } else if (batchStatus.status === 'resolving') {
            console.log('[TimetableImportPage] handleResolveTokens: calling resumeBatch')
            updateBatch(bulkBatchId, { status: 'extracting', unresolvedSessionId: null })

            const resumeResult = await bulkTimetableImportApi.resumeBatch(bulkBatchId)
            console.log('[TimetableImportPage] handleResolveTokens: resumeResult =', resumeResult)

            if (resumeResult.batch_status === 'pending_bulk_shop') {
              const afterResume = nextContinueAction(
                resumeResult.batch_status,
                batchStatus.sections || [],
                true,
              )
              if (afterResume.type === 'import') {
                handleAutoImport(bulkBatchId)
              } else {
                openShopOrImport(bulkBatchId)
              }
            } else if (resumeResult.batch_status === 'resolved') {
              handleAutoImport(bulkBatchId)
            } else if (resumeResult.batch_status === 'completed') {
              const hasImportableSessions = resumeResult.status !== 'running'
              if (hasImportableSessions) {
                handleAutoImport(bulkBatchId)
              } else if (resumeResult.job_id) {
                setBulkJobId(resumeResult.job_id)
                setBulkFlowState('progress')
              } else {
                setBulkFlowState('idle')
              }
            } else if (resumeResult.job_id && resumeResult.batch_status !== 'resolving') {
              setBulkJobId(resumeResult.job_id)
              setBulkFlowState('progress')
            } else {
              const after = await timetableImportV2Api.getBatchStatus(bulkBatchId)
              const pending = (after.sections || []).find(
                (s) => s.status === 'pending_resolution' && s.session_id,
              )
              if (pending?.session_id) {
                setCurrentBatchStatus(after)
                setSessionId(pending.session_id)
                setTokenMatchingBatchInfo(tokenPageBatchInfo({
                  batchId: bulkBatchId,
                  sectionIndex: pending.section_index,
                  sectionIdentifier: pending.section_identifier,
                  totalSections: after.total_sections,
                  startedAt: batchStatus.created_at,
                  resolvedSections: after.resolved_sections,
                }))
                setBulkFlowState('resolving')
                const extractionData = await timetableImportV2Api.getExtractionResult(pending.session_id)
                setCurrentTemplateId(extractionData.updated_template_id || null)
                setMatchedTokens(extractionData.matched_tokens || [])
                setUnmatchedTokens(extractionData.unmatched_tokens || [])
                setTokenPositions(extractionData.token_positions || {})
                setOrphanPatterns(extractionData.orphan_patterns || [])
                orphanPatternsRef.current = extractionData.orphan_patterns || []
                setSplitTracking(extractionData.split_tracking || {})
                setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])
                if (shouldSkipTokenMatching(
                  extractionData.matched_tokens || [],
                  extractionData.unmatched_tokens || [],
                  extractionData.unmatched_header_codes || [],
                )) {
                  await handleAutoResolveAndNavigate(
                    pending.session_id,
                    autoResolutionsFromMatched(extractionData.matched_tokens || []),
                  )
                } else {
                  revealTokenMatching()
                }
              } else if (after.status === 'pending_bulk_shop') {
                openShopOrImport(bulkBatchId)
              } else if (after.status === 'resolved' || after.status === 'partially_failed') {
                handleAutoImport(bulkBatchId)
              } else {
                setBulkFlowState('idle')
              }
            }
          } else if (batchStatus.status === 'resolved' || batchStatus.status === 'partially_failed') {
            console.log('[TimetableImportPage] handleResolveTokens: calling handleAutoImport')
            // All sections resolved - trigger auto-import
            handleAutoImport(bulkBatchId)
          } else if (batchStatus.status === 'completed') {
            console.log('[TimetableImportPage] handleResolveTokens: batch already completed')
            setSuccess('Import bereits abgeschlossen')
            setBulkFlowState('idle')
          } else if (batchStatus.status === 'pending_assignment') {
            console.log('[TimetableImportPage] handleResolveTokens: pending_assignment, calling getBatchPendingStatus')
            console.log('[TimetableImportPage] line 1183: batchStatus.pending_teachers =', batchStatus.pending_teachers)
            const pendingData = await timetableImportV2Api.getBatchPendingStatus(bulkBatchId)
            console.log('[TimetableImportPage] line 1183: pendingData =', pendingData)
            setPendingTeachers(pendingData.pending_teachers || [])
            setPerTeacherCounts(pendingData.per_teacher_counts || {})
            setPerTeacherSessions(pendingData.per_teacher_sessions || {})
            setShowTeacherAssignmentDialog(true)
          } else {
            console.log('[TimetableImportPage] handleResolveTokens: other status, setting progress')
            // Other status - set progress and let polling handle it
            setBulkFlowState('progress')
          }
        } catch (err: any) {
          await handleResumeBatchError(err)
        }
      } else {
        // In single extraction mode, show entries tab and TokenOK dialog
        setActiveTab('entries')
        setShowTokenOK(true)
      }
    } catch (err: any) {
      setIsResolving(false)
      setError('Token-Zuordnung fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
    }
  }, [sessionId, loadTemplates, bulkBatchId, updateBatch, handleResumeBatchError, handleAutoImport, openShopOrImport, setCurrentBatchStatus, setPendingTeachers, setPerTeacherCounts, setPerTeacherSessions, setShowTeacherAssignmentDialog, setBulkFlowState, setBulkJobId])

  const handleV2Import = useCallback(async (validFrom?: string | null, validTo?: string | null) => {
    if (!sessionId) return
    const controller = beginImportRun(false)
    setIsImporting(true)
    setImportProgress(0)
    setImportPages({ done: 0, total: 0 })
    setImportStatus('Import läuft…')
    try {
      const result = await timetableImportV2Api.import(sessionId, parsedEntries, 'all', validFrom, validTo)
      setShowTokenOK(false)
      setShowTokenMatching(false)
      setShowImportValidityDialog(false)
      setImportResult({ imported: result.entries_imported, skipped: result.entries_skipped })
      setTotalImported(prev => prev + result.entries_imported)
      await loadExistingFiles()
      if (result.entries_imported > 0) {
        setSuccess(`Import abgeschlossen: ${result.entries_imported} Einträge importiert`)
      }
      if (result.conflicts?.length > 0) {
        console.warn('Import conflicts:', result.conflicts)
      }
      if (bulkBatchId) {
        setBulkFlowState('resolving')
      } else {
        setActiveTab('entries')
      }
    } catch (err: any) {
      if (controller.signal.aborted || isImportCancelled(err)) return
      setError('Import fehlgeschlagen')
    } finally {
      finishImportRun(controller)
    }
  }, [beginImportRun, finishImportRun, sessionId, parsedEntries, loadExistingFiles, bulkBatchId])

  const handleDeleteEntries = useCallback(async (indices: number[]) => {
    // Delete from backend first (if session exists)
    if (sessionId) {
      try {
        await timetableImportV2Api.deleteSessionEntries(sessionId, indices)
      } catch (err: any) {
        setError('Löschen der Einträge fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
        return
      }
    }

    // Then delete from frontend state
    setParsedEntries(prev => {
      const sorted = [...indices].sort((a, b) => b - a)
      const updated = [...prev]
      for (const idx of sorted) {
        updated.splice(idx, 1)
      }
      sessionStorage.setItem('v2_parsed_entries', JSON.stringify(updated))
      return updated
    })
  }, [sessionId])

  const handleAssignTeacherCode = useCallback(async (code: string, _teacherId: string) => {
    // Teacher code assigned, re-trigger extraction for the pending session
    setShowUnmatchedCodeDialog(false)
    setSuccess(`Code "${code}" zugewiesen. Extrahiere erneut...`)
    
    // Re-trigger extraction for the same file
    if (pendingExtractFileId) {
      try {
        clearV2SessionState()
        
        // Phase 5: Fetch template_id from previous session
        let existingTemplateId: string | undefined = undefined
        try {
          const templateInfo = await bulkTimetableImportApi.getTemplateForFile(pendingExtractFileId)
          if (templateInfo.template_id && templateInfo.confirmed_token_count > 0) {
            existingTemplateId = templateInfo.template_id
          }
        } catch {
          // Ignore
        }
        
        const detect = await bulkTimetableImportApi.detect(pendingExtractFileId)
        if (detect.use_bulk_import) {
          try {
            const result = await startImportWithRetry(pendingExtractFileId, existingTemplateId)
            setBulkJobId(result.job_id)
            setBulkBatchId(result.batch_id)
            setBulkFlowState('progress')
            setSuccess('Vorheriger Import wurde abgebrochen und neu gestartet.')
          } catch (err: any) {
            setError('Fehler beim erneuten Extrahieren: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
          }
        } else {
          setShowTemplateSelection(true)
        }
      } catch {
        setError('Fehler beim erneuten Extrahieren')
      }
    }
  }, [pendingExtractFileId, clearV2SessionState])

  const handleSkipUnmatchedCode = useCallback(() => {
    // Skip - proceed with extraction without teacher code
    setShowUnmatchedCodeDialog(false)
    setSuccess('Seite ohne Lehrer-Code wird fortgesetzt.')
    
    if (pendingSessionId) {
      const sid = pendingSessionId
      setPendingSessionId(null)
      timetableImportV2Api.getExtractionResult(sid).then(extractionData => {
        setCurrentTemplateId(extractionData.updated_template_id || null)
        setSessionId(sid)
        setMatchedTokens(extractionData.matched_tokens || [])
        setUnmatchedTokens(extractionData.unmatched_tokens || [])
        setTokenPositions(extractionData.token_positions || {})
        setOrphanPatterns(extractionData.orphan_patterns || [])
        orphanPatternsRef.current = extractionData.orphan_patterns || []
        setSplitTracking(extractionData.split_tracking || {})
        
        setExistingFiles(prev => prev.map(f =>
          f.id === pendingExtractFileId
            ? { ...f, status: 'structure_ok' as const, is_parsed: true }
            : f
        ))
        
        const matchedTokensList = extractionData.matched_tokens || []
        const unmatchedTokensList = extractionData.unmatched_tokens || []
        if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
          if (matchedTokensList.length === 0) {
            setError('Keine Token extrahiert. Die Datei konnte nicht verarbeitet werden.')
            return
          }
          handleAutoResolveAndNavigate(sid, autoResolutionsFromMatched(matchedTokensList))
        } else {
          revealTokenMatching()
        }
      }).catch(err => {
        setError('Fehler beim Laden der Extraktionsdaten: ' + (err.message || 'Unbekannt'))
      })
    }
  }, [pendingSessionId, pendingExtractFileId, handleAutoResolveAndNavigate])

  const handleLoadEntriesFromBackend = useCallback(async () => {
    if (!sessionId) return
    try {
      const result = await timetableImportV2Api.getResolvedEntries(sessionId)
      if (result.reconstructed_entries && result.reconstructed_entries.length > 0) {
        setParsedEntries(result.reconstructed_entries)
        sessionStorage.setItem('v2_parsed_entries', JSON.stringify(result.reconstructed_entries))
      }
    } catch (err: any) {
      setError('Einträge konnten nicht geladen werden')
    }
  }, [sessionId])

  const handleUpdateEntry = useCallback((index: number, updatedEntry: ReconstructedEntry) => {
    setParsedEntries(prev => {
      const updated = [...prev]
      updated[index] = updatedEntry
      sessionStorage.setItem('v2_parsed_entries', JSON.stringify(updated))
      return updated
    })
  }, [])

  const handleAddEntry = useCallback((entry: ReconstructedEntry) => {
    setParsedEntries(prev => {
      const updated = [...prev, entry]
      sessionStorage.setItem('v2_parsed_entries', JSON.stringify(updated))
      return updated
    })
  }, [])

  const tabs: { key: ExtendedTabType; label: string; icon: typeof FileText }[] = (() => {
    const baseTabs: { key: ExtendedTabType; label: string; icon: typeof FileText }[] = [
      { key: 'files', label: 'Dateien', icon: FolderOpen },
      { key: 'templates', label: 'Templates', icon: Target },
      { key: 'entries', label: `Einträge${parsedEntries.length > 0 ? ` (${parsedEntries.length})` : ''}`, icon: ClipboardList },
      { key: 'timetables', label: 'Stundenpläne', icon: CalendarDays },
      { key: 'spreadsheet', label: 'CSV/Excel Import', icon: FileSpreadsheet },
    ]
    if (isEventoSupported && !loadOnly) {
      baseTabs.push({ key: 'evento', label: 'Evento Sync', icon: Cloud })
    }
    return loadOnly ? baseTabs.filter(tab => tab.key !== 'templates' && tab.key !== 'timetables') : baseTabs
  })()

  const handleTabChange = (tab: ExtendedTabType) => {
    setActiveTab(tab)
    setSearchParams({ tab }, { replace: true })
    // Auto-load entries from backend when navigating to entries tab
    if (tab === 'entries' && sessionId && parsedEntries.length === 0) {
      handleLoadEntriesFromBackend()
    }
  }

  const handleEventoSubTabChange = (subTab: string) => {
    setEventoSubTab(subTab)
    setSearchParams({ tab: 'evento', subtab: subTab }, { replace: true })
  }

  useEffect(() => {
    if (eventoSyncRunning) {
      const timer = setInterval(() => setEventoSyncElapsed(s => s + 1), 1000)
      return () => {
        clearInterval(timer)
        setEventoSyncElapsed(0)
      }
    }
    return undefined
  }, [eventoSyncRunning])

  // Auto-load entries when on entries tab with session but no entries
  useEffect(() => {
    if (activeTab === 'entries' && sessionId && parsedEntries.length === 0) {
      handleLoadEntriesFromBackend()
    }
  }, [activeTab, sessionId, parsedEntries.length, handleLoadEntriesFromBackend])

  return (
    <div className="p-6 max-w-full min-h-[calc(100vh-4rem)] flex flex-col">
      {!isEventoSupported && (
        <div className="p-3 bg-yellow-100 border border-yellow-200 rounded mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <span className="text-yellow-700 text-sm">
            Evento Sync ist nur für Schulen in Schweiz und Liechtenstein verfügbar (CH, LI).
            Ihre Schule ist in: {schoolCountry}
          </span>
        </div>
      )}
      {eventoSyncRunning && (
        <div className="p-3 bg-blue-100 border border-blue-200 rounded mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            <span className="text-blue-700 text-sm">Evento Sync läuft... ({eventoSyncElapsed}s)</span>
          </div>
          <button onClick={() => handleTabChange('evento')} className="text-blue-600 hover:text-blue-700 text-sm">
            Details →
          </button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* V2 Post-Upload Dialog */}
      <PostUploadDialog
        isOpen={showPostUploadDialog}
        fileCount={postUploadFileCount}
        onExtract={async () => {
          setShowPostUploadDialog(false)
          if (!pendingExtractFileId) {
            setError('Keine Datei zum Extrahieren ausgewählt.')
            return
          }
          try {
            const detect = await bulkTimetableImportApi.detect(pendingExtractFileId)
            if (detect.use_bulk_import) {
              const fileInfo = existingFiles.find(f => f.id === pendingExtractFileId)
              const name = fileInfo?.original_filename || ''
              await startBulkImportWithDialog(pendingExtractFileId, name, detect.page_count || 0)
            } else {
              await loadTemplates()
              setShowTemplateSelection(true)
            }
          } catch {
            setError('Fehler beim Erkennen des Dateityps')
          }
        }}
        onContinue={() => setShowPostUploadDialog(false)}
      />

      {/* Unmatched Teacher Code Dialog */}
      <UnmatchedTeacherCodeDialog
        isOpen={showUnmatchedCodeDialog}
        unmatchedCodes={unmatchedCodes}
        onAssign={handleAssignTeacherCode}
        onSkip={handleSkipUnmatchedCode}
      />

      {/* V2 Template Selection */}
      {showTemplateSelection && pendingExtractFileId && (
        <TemplateSelectionDialog
          isOpen={showTemplateSelection}
          templates={templates}
          onResult={(result: SelectionResult) => {
            setShowTemplateSelection(false)
            if (result.type === 'selected') {
              handleV2Extract(pendingExtractFileId, result.templateId)
            } else if (result.type === 'skipped') {
              handleV2Extract(pendingExtractFileId)
            }
          }}
        />
      )}

      {/* Bulk Import Template Selection Dialog */}
      <BulkImportDialog
        isOpen={bulkDialogState.isOpen}
        onClose={() => handleBulkDialogResult({ type: 'cancelled' })}
        fileName={bulkDialogState.fileName}
        pageCount={bulkDialogState.pageCount}
        templates={templates}
        recommendedTemplateId={bulkDialogState.recommendedTemplateId}
        onStart={(templateId: string | null, validFrom?: string | null, validTo?: string | null) => handleBulkDialogResult({ type: 'start', templateId, validFrom, validTo })}
      />

      {/* V2 Extraction Progress */}
      {extractionJobId && (
        <ExtractionProgress
          jobId={extractionJobId}
          onComplete={async (result) => {
            setExtractionJobId(null)
            await loadTemplates()

            // Check for unmatched header teacher codes
            const unmatchedHeaderCodes = (result.result as any)?.unmatched_header_codes || []
            const sid = (result.session_id as string) || (result.result as any)?.session_id

            if (unmatchedHeaderCodes.length > 0 && sid) {
              // Show dialog to assign teacher codes
              setUnmatchedCodes(unmatchedHeaderCodes)
              setPendingSessionId(sid)
              setShowUnmatchedCodeDialog(true)
              return
            }

            // Check for multi-timetable batch
            const batchId = (result.batch_id as string) || (result.result as any)?.batch_id
            const isMultiTimetable = result.is_multi_timetable === true || (result.result as any)?.is_multi_timetable === true

            if (isMultiTimetable && batchId) {
              // Navigate to batch status view
              setBatchId(batchId)
              setPendingExtractFileId(null)
              return
            }

            // Single timetable flow
            const sid2 = (result.session_id as string) || (result.result as any)?.session_id
            if (!sid2) {
              setError('Extraktion fehlgeschlagen: Keine Daten erhalten')
              return
            }
            try {
              const extractionData = await timetableImportV2Api.getExtractionResult(sid2)
              setCurrentTemplateId(extractionData.updated_template_id || null)
              setSessionId(sid2)
              setMatchedTokens(extractionData.matched_tokens || [])
              setUnmatchedTokens(extractionData.unmatched_tokens || [])
              setTokenPositions(extractionData.token_positions || {})
              setOrphanPatterns(extractionData.orphan_patterns || [])
              orphanPatternsRef.current = extractionData.orphan_patterns || []
              setSplitTracking(extractionData.split_tracking || {})
              setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])

              // Phase 3: Log ignored tokens count for debugging
              if (extractionData.ignored_tokens_count > 0) {
                console.log(`[TokenMatching] ${extractionData.ignored_tokens_count} tokens were skipped due to 'ignore' confirmation in template`)
              }

              setExistingFiles(prev => prev.map(f =>
                f.id === pendingExtractFileId
                  ? { ...f, status: 'structure_ok' as const, is_parsed: true }
                  : f
              ))

              const matchedTokensList = extractionData.matched_tokens || []
              const unmatchedTokensList = extractionData.unmatched_tokens || []
              if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
                if (matchedTokensList.length === 0) {
                  setError('Keine Token extrahiert. Die Datei konnte nicht verarbeitet werden.')
                  return
                }
                await handleAutoResolveAndNavigate(sid2, autoResolutionsFromMatched(matchedTokensList))
              } else {
                  const teacherCode = extractionData.matched_tokens?.find(
                    (mt: any) => mt.field === 'teacher_code'
                  )?.token || 'Unbekannt'

                  setTokenMatchingBatchInfo({
                    batchId: `single-${sid2}`,
                    sectionIndex: 0,
                    totalSections: 1,
                    sectionIdentifier: teacherCode,
                    isContinueMode: false,
                    startedAt: new Date().toISOString()
                  })

                  revealTokenMatching()
              }
            } catch (err: any) {
              setError('Fehler beim Laden der Extraktionsdaten: ' + (err.message || 'Unbekannt'))
            }
          }}
          onCancel={async () => {
            try {
              await timetableImportV2Api.cancelJob(extractionJobId)
            } catch {
              // ignore cancel errors
            }
            setExtractionJobId(null)
          }}
        />
      )}

      {/* V2 Batch Status View - only show when not auto-importing */}
      {batchId && !autoImportInProgress && (
        <BatchStatusView
          batchId={batchId}
          onClose={() => {
            setBatchId(null)
            setPendingExtractFileId(null)
          }}
          onImportAll={() => {
            // Remove batch from localStorage after import
            removeBatch(batchId)
            // Refresh files list after import and navigate to timetables
            loadExistingFiles()
            triggerTimetablesReload()
            setActiveTab('timetables')
            setSuccess('Stundenplan-Import erfolgreich abgeschlossen!')
          }}
          onOpenTokenMatching={async (sessionId) => {
            // Use cached status, don't re-fetch
            if (!currentBatchStatus) {
              setError('Batch-Status nicht verfügbar')
              return
            }

            // Find section info from cached status
            const section = currentBatchStatus.sections.find(s => s.session_id === sessionId)
            if (!section) {
              setError('Sitzung nicht im aktuellen Batch gefunden')
              return
            }

            // Determine if continue mode
            const isContinueMode = currentBatchStatus.resolved_sections > 0 ||
                                   currentBatchStatus.imported_sections > 0 ||
                                   localStorage.getItem('token_matching_paused') !== null

            // Clear any paused state since we're continuing (batch is verified valid above)
            localStorage.removeItem('token_matching_paused')

            // Open modal
            setBatchId(null)
            setSessionId(sessionId)
            try {
              const extractionData = await timetableImportV2Api.getExtractionResult(sessionId)
              setCurrentTemplateId(extractionData.updated_template_id || null)
              setMatchedTokens(extractionData.matched_tokens || [])
              setUnmatchedTokens(extractionData.unmatched_tokens || [])
              setTokenPositions(extractionData.token_positions || {})
              setOrphanPatterns(extractionData.orphan_patterns || [])
              orphanPatternsRef.current = extractionData.orphan_patterns || []
              setSplitTracking(extractionData.split_tracking || {})
              setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])

              const matchedTokensList = extractionData.matched_tokens || []
              const unmatchedTokensList = extractionData.unmatched_tokens || []

              if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
                await handleAutoResolveAndNavigate(sessionId, autoResolutionsFromMatched(matchedTokensList))
                return
              }

              setTokenMatchingBatchInfo({
                batchId: batchId!,
                sectionIndex: section.section_index,
                totalSections: currentBatchStatus.total_sections,
                sectionIdentifier: section.section_identifier,
                isContinueMode,
                startedAt: currentBatchStatus.created_at || new Date().toISOString()
              })

              revealTokenMatching()
            } catch (err: any) {
              setError('Fehler beim Laden der Extraktionsdaten: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
            }
          }}
        />
      )}

      {bulkShopBatchId && (
        <BulkShopDialog
          batchId={bulkShopBatchId}
          onDone={() => {
            const id = bulkShopBatchId
            setBulkShopBatchId(null)
            if (id) handleAutoImport(id)
          }}
          onEmpty={() => {
            const id = bulkShopBatchId
            setBulkShopBatchId(null)
            if (id) handleAutoImport(id)
          }}
          onCancel={() => {
            if (bulkShopBatchId) {
              updateBatch(bulkShopBatchId, { status: 'resolving' })
            }
            setBulkShopBatchId(null)
          }}
        />
      )}

      {/* Bulk Import Result with Error Summary */}
      {showBulkResult && (
        <BulkImportResult
          batchId={batchId || bulkBatchId || ''}
          sections={bulkResultSections}
          previewEntries={bulkPreviewEntries}
          onRetry={async (sessionId: string) => {
            try {
              await timetableImportV2Api.retrySession(sessionId)
              setSuccess('Seite erneut extrahiert.')
              // Refresh sections
              const currentBatchId = batchId || bulkBatchId
              if (currentBatchId) {
                const status = await timetableImportV2Api.getBatchStatus(currentBatchId)
                setBulkResultSections(status.sections || [])
                setCurrentBatchStatus(status)
              }
            } catch (err: any) {
              setError('Retry fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
            }
          }}
          onResolve={async (sid: string) => {
            setSessionId(sid)
            setShowBulkResult(false)

            try {
              const extractionData = await timetableImportV2Api.getExtractionResult(sid)
              setCurrentTemplateId(extractionData.updated_template_id || null)
              setMatchedTokens(extractionData.matched_tokens || [])
              setUnmatchedTokens(extractionData.unmatched_tokens || [])
              setTokenPositions(extractionData.token_positions || {})
              setOrphanPatterns(extractionData.orphan_patterns || [])
              orphanPatternsRef.current = extractionData.orphan_patterns || []
              setSplitTracking(extractionData.split_tracking || {})
              setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])

              const matchedTokensList = extractionData.matched_tokens || []
              const unmatchedTokensList = extractionData.unmatched_tokens || []

              if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
                await handleAutoResolveAndNavigate(sid, autoResolutionsFromMatched(matchedTokensList))
                return
              }

              // Find section info for header
              const section = bulkResultSections.find((s: any) => s.session_id === sid)
              if (section) {
                setTokenMatchingBatchInfo({
                  batchId: batchId || bulkBatchId || '',
                  sectionIndex: section.section_index,
                  totalSections: bulkResultSections.length,
                  sectionIdentifier: section.section_identifier,
                  isContinueMode: true,
                  startedAt: currentBatchStatus?.created_at || new Date().toISOString()
                })
              }

              revealTokenMatching()
            } catch (err: any) {
              setError('Fehler beim Laden der Extraktionsdaten: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
            }
          }}
          onImportAll={async () => {
            const currentBatchId = batchId || bulkBatchId
            if (!currentBatchId) return
            const controller = beginImportRun(true)
            setIsImporting(true)
            setImportProgress(0)
            setImportPages({ done: 0, total: 0 })
            setImportStatus('Import läuft…')
            try {
              const importResult = await timetableImportV2Api.importAllSections(currentBatchId, 'resolved_only', reportImportProgress, controller.signal)
              if (controller.signal.aborted) return

              const updatedBatchData = await timetableImportV2Api.getBatchStatus(currentBatchId)
              if (controller.signal.aborted) return
              const pendingData = await timetableImportV2Api.getBatchPendingStatus(currentBatchId)
              if (controller.signal.aborted) return
              const teachers = pendingTeachersForDialog(importResult.pending_teachers, pendingData)

              if (teachers.length > 0) {
                setBulkResultSections(updatedBatchData.sections || [])
                setCurrentBatchStatus(updatedBatchData)
                setBatchId(currentBatchId)
                setPendingTeachers(teachers)
                setPerTeacherCounts(pendingData.per_teacher_counts || {})
                setPerTeacherSessions(pendingData.per_teacher_sessions || {})
                setShowTeacherAssignmentDialog(true)
                if ((importResult.errors || []).some((item) => item.error) || importResult.failed_sections) {
                  setError(importIssueText(importResult))
                }
                setSuccess(`${importResult.imported_sections} Seiten importiert, ${importResult.pending_entries_count || 0} Einträge warten auf Lehrer-Zuordnung.`)
              } else if (importIssueText(importResult)) {
                setError(importIssueText(importResult))
                if (!importResult.imported_sections) {
                  setShowBulkResult(false)
                  setBatchId(null)
                  setBulkFlowState('idle')
                  setBulkJobId(null)
                  setBulkBatchId(null)
                }
              } else {
                // Entries imported successfully - navigate to timetables
                const msg = importResult.skipped_sections > 0
                  ? `${importResult.imported_sections} Seiten importiert, ${importResult.skipped_sections} übersprungen.`
                  : `${importResult.imported_sections} Seiten importiert.`
                setSuccess(msg)
                setShowBulkResult(false)
                setBatchId(null)
                setBulkFlowState('idle')
                setBulkJobId(null)
                setBulkBatchId(null)
                triggerTimetablesReload()
                setActiveTab('timetables')
                loadExistingFiles()
              }
            } catch (err: any) {
              if (controller.signal.aborted || isImportCancelled(err)) return
              setError('Import fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
            } finally {
              finishImportRun(controller)
            }
          }}
          onPreviewEntries={async (sid: string) => {
            try {
              const result = await timetableImportV2Api.getResolvedEntries(sid)
              const entries = result.reconstructed_entries || []
              setBulkPreviewEntries(prev => ({ ...prev, [sid]: entries }))
            } catch {
              // ignore
            }
          }}
          onAssignTeachers={async () => {
            const currentBatchId = batchId || bulkBatchId
            if (!currentBatchId) return
            try {
              const pendingData = await timetableImportV2Api.getBatchPendingStatus(currentBatchId)
              setPendingTeachers(pendingData.pending_teachers || [])
              setPerTeacherCounts(pendingData.per_teacher_counts || {})
              setPerTeacherSessions(pendingData.per_teacher_sessions || {})
              setShowBulkResult(false)
              setShowTeacherAssignmentDialog(true)
            } catch (err: any) {
              setError('Fehler beim Laden der Lehrer-Zuordnung: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
            }
          }}
          onClose={() => {
            setShowBulkResult(false)
            setBatchId(null)
            setBulkFlowState('idle')
            setBulkJobId(null)
            setBulkBatchId(null)
            setActiveTab('timetables')
          }}
        />
      )}

      {/* Teacher Assignment Dialog for pending_assignment */}
      {showTeacherAssignmentDialog && (batchId || bulkBatchId) && (
        <TeacherAssignmentDialog
          batchId={batchId || bulkBatchId || ''}
          pendingTeachers={pendingTeachers}
          perTeacherCounts={perTeacherCounts}
          perTeacherSessions={perTeacherSessions}
          onAssigned={(code) => {
            setPendingTeachers(prev => prev.filter(c => c !== code))
          }}
          onSkipped={(code) => {
            setPendingTeachers(prev => prev.filter(c => c !== code))
          }}
          onSkipAllComplete={async () => {
            // After skip all, mark import as complete without re-running importAllSections
            setShowTeacherAssignmentDialog(false)
            setPendingTeachers([])
            setPerTeacherCounts({})
            setPerTeacherSessions({})
            
            const currentBatchId = batchId || bulkBatchId
            if (!currentBatchId) return
            
            try {
              const batchStatus = await timetableImportV2Api.getBatchStatus(currentBatchId)
              const importedCount = batchStatus.imported_sections || 0
              const skippedCount = batchStatus.skipped_sections || 0
              const msg = skippedCount > 0
                ? `${importedCount} Seiten importiert, ${skippedCount} übersprungen.`
                : `${importedCount} Seiten importiert.`
              setSuccess(msg)
              setBatchId(null)
              setBulkBatchId(null)
              setCurrentBatchStatus(null)
              autoImportInProgressRef.current = false
              setAutoImportInProgress(false)
              setBulkFlowState('idle')
              setBulkJobId(null)
              triggerTimetablesReload()
              loadExistingFiles()
              setActiveTab('timetables')
            } catch (err: any) {
              setError('Fehler beim Abschließen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
              setBatchId(null)
              setBulkBatchId(null)
            }
          }}
          onClose={() => {
            setShowTeacherAssignmentDialog(false)
            // Remove batch from persistence so it doesn't re-open via recovery banner
            const currentBatchId = batchId || bulkBatchId
            if (currentBatchId) {
              removeBatch(currentBatchId)
              setBatchId(null)
              setBulkBatchId(null)
              autoImportInProgressRef.current = false
              setAutoImportInProgress(false)
              setBulkFlowState('idle')
              setBulkJobId(null)
            }
            setPendingTeachers([])
            setPerTeacherCounts({})
            setPerTeacherSessions({})
          }}
          onComplete={() => {
            setShowTeacherAssignmentDialog(false)
            setPendingTeachers([])
            setPerTeacherCounts({})
            setPerTeacherSessions({})
            
            const currentBatchId = batchId || bulkBatchId
            if (!currentBatchId) return
            const controller = beginImportRun(false)
            
            setIsImporting(true)
            setImportProgress(0)
            setImportPages({ done: 0, total: 0 })
            setImportStatus('Import läuft…')
            
            const handleImportComplete = async () => {
              try {
                const batchStatus = await timetableImportV2Api.getBatchStatus(currentBatchId)
                if (controller.signal.aborted) return
                
                const importedCount = batchStatus.imported_sections || 0
                const failedCount = batchStatus.failed_sections || 0
                
                // Don't call importAllSections - entries were already imported by assign_teacher_to_user
                // Just show the final result based on batch status
                const msg = failedCount > 0
                  ? `${importedCount} Seiten importiert, ${failedCount} fehlgeschlagen.`
                  : `${importedCount} Seiten importiert.`
                setSuccess(msg)
                setBatchId(null)
                setBulkBatchId(null)
                setCurrentBatchStatus(null)
                autoImportInProgressRef.current = false
                setAutoImportInProgress(false)
                setBulkFlowState('idle')
                setBulkJobId(null)
                triggerTimetablesReload()
                loadExistingFiles()
                setActiveTab('timetables')
              } catch (err: any) {
                if (controller.signal.aborted || isImportCancelled(err)) return
                setError('Fehler beim Import: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
                setBatchId(null)
                setBulkBatchId(null)
              } finally {
                finishImportRun(controller)
              }
            }
            
            handleImportComplete()
          }}
        />
      )}

      {/* Batch Conflict Dialog */}
      {showConflictDialog && conflictExistingBatch && (
        <BatchConflictDialog
          isOpen={showConflictDialog}
          existingBatch={conflictExistingBatch}
          newFileName={conflictNewFileName}
          onContinueExisting={handleConflictContinue}
          onRestartNew={handleConflictRestart}
          onCancel={handleConflictCancel}
        />
      )}

      {/* V2 Token Matching Modal */}
      <TokenMatchingModal
        isOpen={showTokenMatching && !loadOnly}
        sessionId={sessionId || undefined}
        templateId={currentTemplateId || undefined}
        matchedTokens={matchedTokens}
        unmatchedTokens={unmatchedTokens}
        tokenPositions={tokenPositions}
        orphanPatterns={orphanPatterns}
        splitTracking={splitTracking}
        unmatchedHeaderCodes={unmatchedHeaderCodes}
        onResolve={handleResolveTokens}
        onCancel={handleTokenMatchingCancel}
        isResolving={isResolving}
        batchInfo={tokenMatchingBatchInfo}
        onPause={handleTokenMatchingPause}
      />

      {/* V2 OK Dialog */}
      <TokenOKDialog
        isOpen={showTokenOK}
        matchedCount={matchedTokens.length}
        matchedTokens={matchedTokens}
        onPreview={() => {
          setShowTokenOK(false)
          // Return to resolving state if we're in bulk import flow
          if (bulkBatchId) {
            setBulkFlowState('resolving')
          } else {
            setActiveTab('entries')
          }
        }}
        onImport={() => {
          if (bulkBatchId) {
            handleV2Import()
          } else {
            setShowTokenOK(false)
            setShowImportValidityDialog(true)
          }
        }}
        onClose={() => {
          setShowTokenOK(false)
          if (bulkBatchId) {
            setBulkFlowState('resolving')
          }
        }}
      />

      {/* Solution 3: Import Validity Dialog with Presets */}
      {showImportValidityDialog && !bulkBatchId && (
        <ImportValidityDialog
          isOpen={showImportValidityDialog}
          title="Gültigkeitsbereich festlegen"
          description="Wähle den Gültigkeitsbereich für diesen Stundenplan:"
          entryCount={parsedEntries.length}
          teacherCode={parsedEntries[0]?.teacher_code || undefined}
          onConfirm={(validFrom, validTo) => {
            setShowImportValidityDialog(false)
            handleV2Import(validFrom, validTo)
          }}
          onCancel={() => {
            setShowImportValidityDialog(false)
          }}
        />
      )}

      {/* Bulk Import: Progress */}
      {bulkFlowState === 'progress' && bulkJobId && bulkBatchId && (
        <BulkImportProgress
          jobId={bulkJobId}
          batchId={bulkBatchId}
          onCancel={() => {
            console.log('[TimetableImportPage] Import cancelled by user')
            setBulkFlowState('idle')
            setBulkJobId(null)
            autoImportInProgressRef.current = false
            setAutoImportInProgress(false)
          }}
          onCompleted={async (batchId: string) => {
            setBulkFlowState('idle')
            try {
              const batchData = await timetableImportV2Api.getBatchStatus(batchId)
              setCurrentBatchStatus(batchData)
              const completedAction = nextContinueAction(
                batchData.status,
                batchData.sections || [],
                Boolean(bulkJobId),
              )
              if (completedAction.type === 'shop') {
                openShopOrImport(batchId)
                return
              }
              if (completedAction.type === 'import') {
                handleAutoImport(batchId)
                return
              }
              const sections = batchData.sections || []
              const unresolved = sections.find(
                (s) => s.status === 'pending_resolution' && ((s.matched_count ?? 0) > 0 || (s.unmatched_count ?? 0) > 4)
              ) || sections.find(
                (s) => s.status === 'pending_resolution' || (s.has_unmatched && s.session_id)
              )
              if (unresolved?.session_id) {
                // Update localStorage - batch awaiting resolution
                updateBatch(batchId, {
                  status: 'awaiting_resolution',
                  resolvedSections: batchData.resolved_sections,
                  totalSections: batchData.total_sections,
                  unresolvedSessionId: unresolved.session_id,
                })

                setSessionId(unresolved.session_id)

                // NEW: Set batch info for auto-pause flow
                const pausedState = localStorage.getItem('token_matching_paused')
                const isContinueMode = batchData.resolved_sections > 0 || pausedState !== null

                setTokenMatchingBatchInfo(tokenPageBatchInfo({
                  batchId,
                  sectionIndex: unresolved.section_index ?? 0,
                  sectionIdentifier: unresolved.section_identifier,
                  totalSections: batchData.total_sections,
                  startedAt: batchData.created_at,
                  isContinueMode,
                  resolvedSections: batchData.resolved_sections,
                }))

                // Clear paused state
                localStorage.removeItem('token_matching_paused')

                const extractionData = await timetableImportV2Api.getExtractionResult(unresolved.session_id)
                setCurrentTemplateId(extractionData.updated_template_id || null)
                setMatchedTokens(extractionData.matched_tokens || [])
                setUnmatchedTokens(extractionData.unmatched_tokens || [])
                setTokenPositions(extractionData.token_positions || {})
                setOrphanPatterns(extractionData.orphan_patterns || [])
                orphanPatternsRef.current = extractionData.orphan_patterns || []
                setSplitTracking(extractionData.split_tracking || {})
                setUnmatchedHeaderCodes(extractionData.unmatched_header_codes || [])

              const matchedTokensList = extractionData.matched_tokens || []
              const unmatchedTokensList = extractionData.unmatched_tokens || []

              if (shouldSkipTokenMatching(matchedTokensList, unmatchedTokensList, extractionData.unmatched_header_codes || [])) {
                    await handleAutoResolveAndNavigate(unresolved.session_id, autoResolutionsFromMatched(matchedTokensList))
                    return
                  }

                revealTokenMatching()

                // Keep bulkFlowState as 'resolving' - DON'T clear bulkBatchId!
                setBulkFlowState('resolving')

                // DO NOT clear bulkJobId or bulkBatchId - needed for resume!
                return
              } else {
                // All sections resolved - auto-import immediately
                const controller = beginImportRun(true)
                setIsImporting(true)
                setImportProgress(0)
                setImportPages({ done: 0, total: 0 })
                setImportStatus('Import läuft…')
                try {
                  const importResult = await timetableImportV2Api.importAllSections(batchId, 'resolved_only', reportImportProgress, controller.signal)
                  if (controller.signal.aborted) return

                  const updatedBatchData = await timetableImportV2Api.getBatchStatus(batchId)
                  if (controller.signal.aborted) return
                  const pendingData = await timetableImportV2Api.getBatchPendingStatus(batchId)
                  if (controller.signal.aborted) return
                  const teachers = pendingTeachersForDialog(importResult.pending_teachers, pendingData)

                  if (teachers.length > 0) {
                    setBulkResultSections(updatedBatchData.sections || [])
                    setCurrentBatchStatus(updatedBatchData)
                    setBatchId(batchId)
                    setPendingTeachers(teachers)
                    setPerTeacherCounts(pendingData.per_teacher_counts || {})
                    setPerTeacherSessions(pendingData.per_teacher_sessions || {})
                    setShowTeacherAssignmentDialog(true)
                    if ((importResult.errors || []).some((item) => item.error) || importResult.failed_sections) {
                      setError(importIssueText(importResult))
                    }
                    setSuccess(`${importResult.imported_sections} Seiten importiert, ${importResult.pending_entries_count || 0} Einträge warten auf Lehrer-Zuordnung.`)
                  } else if (importIssueText(importResult)) {
                    setError(importIssueText(importResult))
                  } else {
                    const msg = importResult.skipped_sections > 0
                      ? `${importResult.imported_sections} Seiten importiert, ${importResult.skipped_sections} übersprungen.`
                      : `${importResult.imported_sections} Seiten importiert.`
                    setSuccess(msg)
                    setActiveTab('timetables')
                    loadExistingFiles()
                    loadTemplates()
                  }
                } catch (err: any) {
                  if (controller.signal.aborted || isImportCancelled(err)) return
                  setError('Auto-Import fehlgeschlagen: ' + (err.response?.data?.detail || (err.response?.data?.details as string[] | undefined)?.join('; ') || err.response?.data?.message || err.message))
                } finally {
                  finishImportRun(controller)
                }
              }
            } catch {
              setError('Fehler beim Laden der Extraktionsdaten')
            }
            // Only clear state if batch is fully complete (no unresolved) and no errors
            if (!showBulkResult) {
              setBulkFlowState('idle')
              setBulkJobId(null)
              setBulkBatchId(null)
            }
          }}
        />
      )}

      <div className="bg-white rounded-lg border flex-1 flex flex-col">
        <div className="flex border-b">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`px-8 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </div>

        <div className="p-6 flex-1 overflow-auto">
            {activeTab === 'files' && (
              <FilesTab
                files={existingFiles}
                loading={filesLoading}
                total={fileTotal}
                page={filePage}
                pageSize={pageSize}
                search={fileSearch}
                statusFilter={fileStatusFilter}
                onUpload={handleFileUpload}
                onExtract={async (fileId) => {
                  clearV2SessionState()
                  try {
                    const detect = await bulkTimetableImportApi.detect(fileId)
                    if (detect.use_bulk_import) {
                      const fileInfo = existingFiles.find(f => f.id === fileId)
                      const name = fileInfo?.original_filename || ''
                      await startBulkImportWithDialog(fileId, name, detect.page_count || 0)
                    } else {
                      setPendingExtractFileId(fileId)
                      setShowTemplateSelection(true)
                    }
                  } catch {
                    setError('Fehler beim Erkennen des Dateityps')
                  }
                }}
                onBulkExtract={handleBulkExtract}
                onDelete={handleDeleteFile}
                onBulkDelete={handleBulkDelete}
                onSelectForImport={async (fileId) => {
                  clearV2SessionState()
                  try {
                    const detect = await bulkTimetableImportApi.detect(fileId)
                    if (detect.use_bulk_import) {
                      const fileInfo = existingFiles.find(f => f.id === fileId)
                      const name = fileInfo?.original_filename || ''
                      await startBulkImportWithDialog(fileId, name, detect.page_count || 0)
                    } else {
                      setPendingExtractFileId(fileId)
                      setShowTemplateSelection(true)
                    }
                  } catch {
                    setError('Fehler beim Erkennen des Dateityps')
                  }
                }}
                onSearchChange={setFileSearch}
                onStatusFilterChange={setFileStatusFilter}
                onPageChange={setFilePage}
                onContinueBatch={handleContinueBatch}
              />
            )}
              {!loadOnly && activeTab === 'templates' && (
                <TemplatesTab
                  templates={templates}
                  loading={templatesLoading}
                  search={templateSearch}
                  onSearchChange={setTemplateSearch}
                  onDeleteTemplate={handleDeleteTemplate}
                  onDeleteAllTemplates={handleDeleteAllTemplates}
                  onTemplateUpdated={loadTemplates}
                  onGoToFiles={() => handleTabChange('files')}
                />
             )}
                {activeTab === 'entries' && (
                    <EntriesTab
                      entries={parsedEntries}
                      onImport={() => {
                        if (bulkBatchId) {
                          handleV2Import()
                        } else {
                          setShowImportValidityDialog(true)
                        }
                      }}
                      isImporting={isImporting}
                      importResult={importResult || undefined}
                      totalImported={totalImported}
                      onDeleteEntries={handleDeleteEntries}
                      onUpdateEntry={handleUpdateEntry}
                      onAddEntry={handleAddEntry}
                      sessionId={sessionId}
                      onLoadEntries={handleLoadEntriesFromBackend}
                      isLoadingEntries={false}
                      pendingExtractFileId={pendingExtractFileId}
                      pendingExtractFileType="pdf"
                     />
                )}
                {activeTab === 'timetables' && (
                  <TimetablesTab
                    key={timetablesRefreshKey}
                    reloadKey={timetablesRefreshKey}
                    onDeleteSuccess={() => loadExistingFiles()}
                  />
                )}
  {activeTab === 'spreadsheet' && (
              <SpreadsheetImportTab
                allowTemplateWrite={!loadOnly}
                onImportComplete={() => {
                  setSuccess('CSV/Excel Import abgeschlossen!')
                  setActiveTab('files')
                  loadExistingFiles()
                }}
                onGoToFiles={() => setActiveTab('files')}
              />
            )}
             {!loadOnly && activeTab === 'evento' && isEventoSupported && (
              <EventoSyncTab
                initialSubTab={eventoSubTab as 'config' | 'monitor' | 'keys' | undefined}
                onSubTabChange={handleEventoSubTabChange}
                onSyncRunning={setEventoSyncRunning}
              />
            )}
             {!loadOnly && activeTab === 'evento' && !isEventoSupported && (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Evento nicht verfügbar</h3>
                <p className="text-gray-500 mb-4">
                  Evento Sync ist nur für Schweizer Schulen verfügbar.
                  Ihre Schule ist in: {schoolCountry}
                </p>
              </div>
            )}

          {/* Global import loading overlay - token-fix-v6 */}
          {(isImporting || autoImportInProgress) && (
            <ImportRunDialog
              progress={importProgress}
              message={importStatus}
              pagesDone={importPages.done}
              pagesTotal={importPages.total}
              onCancel={importCancelable ? cancelImportRun : undefined}
              cancelling={importCancelling}
            />
          )}
          </div>
       </div>
    </div>
  )
}
